import * as d3 from "d3";
import { DateTime } from "luxon";
import { capitalize, pct, getLastItem, pluralize, groupBy } from "./utils";
import { initCountries, updateSelectedCountry, parseProvincesAndCountries, toggleSearch } from "./locations";
import { initializeReadout, showWeekDataReadout, showDayDataReadout, showWeeklyDateReadout, showDailyDateReadout } from "./readout";
import { initializeState, setLocalStorage, clearLocalStorage, getQueryVariable } from "./state";
import { initPdf } from "./pdf";
import { palette } from "./palette";
import chroma from "chroma-js";
import { Quadtree } from "./quadtree";
import { fetchData, fetchPredictions, getLineageToVariantLookup, getMostRecentDataDate, getUpdateDate, setDataCopy, getDataCopy, gatherOtherBucket, getSubvariantTotals, getValidLineages, getSubvariantsGroupedByVariants, setupKeys, sortKeys } from "./data";
import lineagesJson from './data/lineages.json';
import {
  CHART_DATE_FORMAT_MONTH_SHORT,
  CHART_DATE_FORMAT_MONTH,
  CHART_DATE_FORMAT_DATE_SHORT,
  CHART_DATE_FORMAT_YEAR_SHORT,
  YAXIS_TICKS_APPROXIMATE,
  UNCERTAIN_THRESHOLD,
  MOUSE_PROXIMITY,
  PROVINCE_SEPARATOR,
  MAX_LOCATIONS_GRID_MODE
} from "./constants";

// import colorTableJson from './data/colorTable.json';
let colorTableJson = {};
let colorTableJsonURL = '/static/colorTable.json';
// await fetch('/static/colorTable.json')
//   .then(res => res.json())
//   .then(res => {
//     colorTableJson = res;
//   });

let countries = ['USA'];
let timeframe = '4months', frequency;

let chartMode = `pct`; // default
let predictionMode = true; // default
let noPredictionData = false;
let gridView = false;
let noLocalStorageUpdate = false;

let graphicTemplate, axisTemplate, featureTemplate, readoutTemplate, countrySearchTemplate;

let lineageToVariantLookup = {};

let startDate, endDate, mostRecentDataDate;
let xAxis;
//let graphicContainerBoundingBox;
let quadtree;

let useStaticPalette = false;

let LINEAGE_THRESHOLD;


let hideLoader = ()=>{
  document.body.classList.remove('loading');
};
let showLoader = ()=>{
  document.body.classList.add('loading');
};
let updateLoaderMessage = (msg) => {
  document.querySelector("#loading-message").innerHTML = msg;
}




// Initialization

initialize();

function initialize() {

  if (getQueryVariable("useStaticPalette") || getQueryVariable("usestaticpalette")) {
    useStaticPalette = true;
  }

  if (getQueryVariable("noLocalStorageUpdate") || getQueryVariable("nolocalstorageupdate")) {
    noLocalStorageUpdate = true;
  }


  palette.initialize();

  initializeState(getCountries, setCountries, getChartMode, setChartMode);
  initializeReadout(getDisplayName, getLineageColor, predictionMode && chartMode !== 'bar');

  initCountries(setCountry, getCountries, setCountries, changeGridMode);


  graphicTemplate = document.querySelector("#graphic").cloneNode(true);
  axisTemplate = document.querySelector("#axis").cloneNode(true);
  featureTemplate = document.querySelector("#feature").cloneNode(true);
  readoutTemplate = document.querySelector("#readout").cloneNode(true);
  countrySearchTemplate = document.querySelector("#geographer").cloneNode(true);

  initSubvariantsSlider();
  changeChartMode(chartMode, noLocalStorageUpdate);
  loadCharts();

  addEvents();

}


// Called in loadCharts, after all data is processed
function dataInitialize(data) {

  lineageToVariantLookup = getLineageToVariantLookup();
  mostRecentDataDate = getMostRecentDataDate();

  setDataCopy(data);
  data = filterData(data);

  // Clear these -- they may be triggered below
  clearError();
  noPredictionData = false;

  fetchPredictions(countries, () => {

    let {
      validLineages, allLineages, underThresholdLineages, validVariants, allVariants, underThresholdVariants
    } = getValidLineages(data, LINEAGE_THRESHOLD);

    let { groupedByVariants, groupedByVariantsOthers, groupedByVariantsFilteredByPredictions, groupedByVariantsOthersFilteredByPredictions } = getSubvariantsGroupedByVariants(data, LINEAGE_THRESHOLD);

    data.locations.forEach(location => {
      // location.validLineages = validLineages;
      // location.allLineages = allLineages;
      // location.underThresholdLineages = underThresholdLineages;
      // location.validVariants = validVariants;
      // location.allVariants = allVariants;
      // location.underThresholdVariants = underThresholdVariants;

      location.groupedByVariants = groupedByVariants;
      location.groupedByVariantsOthers = groupedByVariantsOthers;

      location.groupedByVariantsFilteredByPredictions = groupedByVariantsFilteredByPredictions;
      location.groupedByVariantsOthersFilteredByPredictions = groupedByVariantsOthersFilteredByPredictions;
    });

    fetch(colorTableJsonURL, {cache: "no-cache"})
      .then(res => res.json())
      .then(res => {
        colorTableJson = res;
      })
      .then(() => {

        initPdf(
          data,
          pdfChangeChartMode,
          getChartMode,
          redraw,
          drawCurveChart,
          getDisplayName,
          getLineageColor,
          showLoader,
          hideLoader,
          updateLoaderMessage,
          LINEAGE_THRESHOLD,
          startDate,
          endDate,
          getUpdateDate()
        );
        initVariantColorKeys(data);
        redraw(data);

      });

  }, data, LINEAGE_THRESHOLD, () => {
    // Handle a failed set of predictions
    //noPredictionData = true;

    //handleErrorPredictions();

    // if (chartMode !== 'num') {
    //
    //   changeChartMode('num');
    //   initializeReadout(getDisplayName, getLineageColor, predictionMode && chartMode !== 'bar');
    //   //dataInitialize(getDataCopy());
    //   redraw(getDataCopy());
    //
    // }
  });

}

function redraw(data) {
  setDataCopy(data);
  data = filterData(data);

  //initVariantColorKeys(data);
  //initChartVariants(data);
  //initVariant();

  setTimeframeDates(data);

  draw(data);
  updateDataFlags(data);
}


// General UI Events, attached exactly once

function addEvents() {

  window.addEventListener("resize", () => {
    //graphicContainerBoundingBox = document.querySelector("#graphic").getBoundingClientRect();

    updateGraphicContainerBoundingBox(document.querySelector("#graphic"));
  });

  setCurveChartOneTimeListeners();

  // Chart options
  document.querySelectorAll("#chart-toggle .chart-opt").forEach(button => {
    button.addEventListener("click", () => {
      showLoader(); // Ugh, but the transitions are smoother
      changeChartMode(button.getAttribute("data-chart-mode"));
      initializeReadout(getDisplayName, getLineageColor, predictionMode && chartMode !== 'bar');
      //dataInitialize(getDataCopy());
      redraw(getDataCopy());
    });
  });
  let input = document.querySelector("#prediction");
  input.addEventListener("click", () => {
    togglePrediction(input.checked);

    // Keep them in sync
    document.querySelector("#mobile-prediction").checked = input.checked;
  });

  let mobileInput = document.querySelector("#mobile-prediction");
  mobileInput.addEventListener("click", () => {
    togglePrediction(mobileInput.checked);

    // Keep them in sync
    document.querySelector("#prediction").checked = mobileInput.checked;
  });

  // Mobile
  document.querySelectorAll("#mobile-chart-mode .chart-opt").forEach(button => {
    button.addEventListener("click", () => {
      changeChartMode(button.getAttribute("data-chart-mode"));
      initializeReadout(getDisplayName, getLineageColor, predictionMode && chartMode !== 'bar');
      //dataInitialize(getDataCopy());
      redraw(getDataCopy());
    });
  });

  document.querySelectorAll("#categories .tab").forEach(tab => {
    tab.addEventListener("click", () => {
      setTab(tab);
    });
    // tab.addEventListener("touchstart", () => {
    //   setTab(tab);
    // });
  });

  document.querySelector("body").addEventListener("click", hideThresholdSlider, false);
  document.querySelector("#threshold-slider-label").addEventListener("click", () => {
    showThresholdSlider();
  });

  document.querySelector("#clear-local-storage").addEventListener("click", () => {
    clearLocalStorage();
    location.reload();
  });




}

function changeGridMode(isGrid) {
  gridView = isGrid || false;
  //console.log('gridView', gridView);
}

function pdfChangeChartMode(mode) {
  changeChartMode(mode, true);
  initializeReadout(getDisplayName, getLineageColor, predictionMode && chartMode !== 'bar');
  redraw(getDataCopy());
}

function changeChartMode(buttonMode, noLocalStorage) {

  let mode = buttonMode;

  // let modes = ["pct", "bar", "curve"];

  if (mode !== chartMode) {

    document.body.classList.toggle("bar", false);
    document.body.classList.toggle("pct", false);
    document.body.classList.toggle("curve", false);

    document.querySelector("#bar-opt").classList.toggle("active", false);
    document.querySelector("#pct-opt").classList.toggle("active", false);
    document.querySelector("#curve-opt").classList.toggle("active", false);

    document.querySelector("#mobile-bar-opt").classList.toggle("active", false);
    document.querySelector("#mobile-pct-opt").classList.toggle("active", false);
    document.querySelector("#mobile-curve-opt").classList.toggle("active", false);

  }

  // if (forceMode) {

  //   mode = chartMode;

  // } else {
  //   // Let's set the mode as if it's a toggle, not a button press
  //   let currentModeIndex = modes.indexOf(chartMode);

  //   currentModeIndex++;
  //   if (currentModeIndex >= modes.length) {
  //     currentModeIndex = 0;
  //   }

  //   mode = modes[currentModeIndex];
  // }

  document.querySelector("#prediction").classList.toggle("hidden", false);
  document.querySelector("#mobile-prediction-detail").classList.toggle("hidden", false);

  if (mode === "pct") {
    document.body.classList.toggle("pct", true);
    document.querySelector("#pct-opt").classList.toggle("active", true);
    document.querySelector("#mobile-pct-opt").classList.toggle("active", true);
  } else if (mode === "bar") {
    document.body.classList.toggle("bar", true);
    document.querySelector("#bar-opt").classList.toggle("active", true);
    document.querySelector("#mobile-bar-opt").classList.toggle("active", true);
    document.querySelector("#mobile-prediction-detail").classList.toggle("hidden", true);
  } else if (mode === "curve") {
    document.body.classList.toggle("curve", true);
    document.querySelector("#curve-opt").classList.toggle("active", true);
    document.querySelector("#mobile-curve-opt").classList.toggle("active", true);

    document.querySelector("#prediction").classList.toggle("hidden", true);
    document.querySelector("#mobile-prediction-detail").classList.toggle("hidden", true);
  }

  setChartMode(mode);

  if (!noLocalStorage) {
    setLocalStorage();
  }

  // initializeReadout(getDisplayName, getLineageColor, predictionMode);
  // dataInitialize(getDataCopy());
}

function togglePrediction(prediction) {

  //let predictionStacks = document.querySelectorAll(".stack.prediction");

  if (prediction) {

    // predictionStacks.forEach(stack => {
    //   stack.classList.toggle("hide", false);
    // });

    predictionMode = true;
    initializeReadout(getDisplayName, getLineageColor, predictionMode && chartMode !== 'bar');
    redraw(getDataCopy());

  } else {

    // predictionStacks.forEach(stack => {
    //   stack.classList.toggle("hide", true);
    // });

    predictionMode = false;
    initializeReadout(getDisplayName, getLineageColor, predictionMode && chartMode !== 'bar');
    redraw(getDataCopy());

  }

}

function setTab(thisTab) {

  let gallery = document.querySelector("#gallery");

  let tabs = gallery.querySelectorAll(".tab");

  tabs.forEach((tab) => {
    let tabClass = tab.getAttribute("data-order");
    gallery.classList.toggle(tabClass, false);

    tab.classList.toggle("highlight", false);
  });

  gallery.classList.toggle(thisTab.getAttribute("data-order"), true);

}

let timesClicked = 0;

function initSubvariantsSlider() {

  let slider = document.querySelector('#threshold-slider');
  LINEAGE_THRESHOLD = slider.value;

  slider.addEventListener("input", () => {

    showLoader();

    LINEAGE_THRESHOLD = slider.value;

    let label = document.querySelector("#threshold-slider-label");
    label.innerHTML = pct(LINEAGE_THRESHOLD, 0, true, true);

    let data = getDataCopy();
    dataInitialize(data);

  });

}

function showThresholdSlider() {
  document.querySelector("#categories-threshold-slider").classList.toggle("active", true);
  document.querySelector("#threshold-slider-label").classList.toggle("active", true);
  document.querySelector("#categories-threshold-slider").addEventListener("click", (ev) => {
    ev.stopPropagation();
  }, false);
}

function hideThresholdSlider() {
  timesClicked++;
  if (timesClicked > 1) {
    document.querySelector("#categories-threshold-slider").classList.toggle("active", false);
    document.querySelector("#threshold-slider-label").classList.toggle("active", false);
    timesClicked = 0;
  }
}





// Variant and Subvariant Buttons
// Draws buttons for Variants and Subvariants
function drawButtons(data) {

  let locationData = data.locations[0];

  // console.log(locationData);

  let groupedByVariants, groupedByVariantsOthers;

  if (chartMode === 'pct' || chartMode === 'curve') {
    groupedByVariants = locationData.groupedByVariantsFilteredByPredictions;
    groupedByVariantsOthers = locationData.groupedByVariantsOthersFilteredByPredictions;
  } else {
    groupedByVariants = locationData.groupedByVariants;
    groupedByVariantsOthers = locationData.groupedByVariantsOthers;
  }



  // Draw the subvariant buttons

  let featuredHtml = ``;

  let featuredSubvariantContainer = document.querySelector("#multiples--featured-subvariants");

  // NOTE 9.12.22 (paul) disabled for now, see note below
  // let featuredSubvariantsTab = document.querySelector("#categories-readout");

  // NOTE 9.12.22 (paul) turned off for now, value will be split up and reassigned to sections below
  // let featuredSubvariantsCountLabel = featuredSubvariantsTab.querySelector(".tab-count");

  // let featuredSubvariantCount = 0;

  // Get everything ready for display below

  let otherSvs = [];

  let variants = Object.keys(groupedByVariants).filter(v => v !== "Unassigned").map(v => {
    let svs = groupedByVariants[v].map(sv => {
      return {...sv, ...locationData.subvariantTotals[sv.name]};
    });

    // NOTE 1.6.23 (tim) Filter out lineages that don't appear on modeled charts

    // if (chartMode === 'pct' || chartMode === 'curve') {
    //   svs = svs.filter(sv => {
    //     let include = false;
    //     data.locations.forEach(l => {
    //       if (l.predictionSubvariants.includes(sv.name)) {
    //         include = true;
    //       }
    //     });
    //     return include;
    //   });
    // }


    return {
      variant : v,
      subvariants : svs,
      subvariantCount : svs.length,
      totalCount : 0
    }
  });


  let others = {
    variant : 'other',
    subvariants : [{
      name : 'other',
      ...locationData.subvariantTotals['other']
    }],
    subvariantCount : 0,
    totalCount : 0
  };
  for (const otherVariant in groupedByVariantsOthers) {
    others.subvariantCount += groupedByVariantsOthers[otherVariant].length;
  }
  variants.push(others);


  variants.forEach(v => {
    v.subvariants.forEach(sv => {
      v.totalCount += sv.num;
    });
  });

  //variants.sort((a, b) => b.totalCount - a.totalCount);

  variants.forEach((variant) => {

    let columnize = false;
    let columnizeCounter = 0;
    let rowsPerColumn = 3;

    //let subvariants = groupedByVariants[variant] || [];
    let subvariants = variant.subvariants;
    if (subvariants.length >= rowsPerColumn) {
      columnize = true;
    }

    // featuredSubvariantCount += subvariants.length;

    let subvariantsGroupedByLevels = {};

    subvariants.forEach(subvariant => {

      let level = subvariant.secondLevel;
      if (subvariant.variant && subvariant.variant.name && subvariant.variant.name.match(/delta/gi)) {
        level = subvariant.topLevel;
      }

      subvariantsGroupedByLevels[level] = subvariantsGroupedByLevels[level] || {
        'totalCount' : 0,
        'subvariants' : []
      }

      subvariantsGroupedByLevels[level].subvariants.push(subvariant);
      subvariantsGroupedByLevels[level].totalCount += subvariant.num;

    });

    let subvariantsByLevel = Object.values(subvariantsGroupedByLevels);
    subvariantsByLevel.sort((a, b) => b.totalCount - a.totalCount);

    // console.log(subvariantsByLevel);

    subvariantsByLevel.forEach(level => {
      level.subvariants.sort((a, b) => b.num - a.num);
    });

    subvariantsByLevel.forEach((level, levelCounter) => {

      if (levelCounter <= subvariantsByLevel.length - 1 ) {
        if (levelCounter === 0) {
          featuredHtml += `<div class="rule start"></div>`;
        } else {
          featuredHtml += `<div class="rule"></div>`;
        }
      }

      if (levelCounter === 0) {
        featuredHtml += `<div class="subvariant-group start">`;
      } else {
        featuredHtml += `<div class="subvariant-group">`;
      }

      columnizeCounter = 0;

      featuredHtml += `<h4 class="variant-name" data-variant="${ variant.variant }"><span class="label">${ getDisplayName(variant.variant) }</span>&ensp;<span class="count">(${ variant.subvariantCount.toLocaleString() } ${ pluralize('lineage', variant.subvariantCount)})</span></h4>`;

      featuredHtml += `<div class="subvariant-group-inner">`;

      level.subvariants.forEach((subvariant, subvariantCounter) => {

        if ((columnize) && (columnizeCounter % rowsPerColumn === 0)) {
          featuredHtml += `<div class="subvariant-column">`;
        }

        let subvariantTotal = 0;
        if (locationData.subvariantTotals[subvariant.name]) {
          subvariantTotal = locationData.subvariantTotals[subvariant.name].num;
        }
        let subvariantDisplay = getDisplayName(subvariant.name);

        if (variant.variant.match(/other/gi)) {
          subvariantDisplay = `${ variant.subvariantCount.toLocaleString() } ${ pluralize('lineage', variant.subvariantCount)}`;
        }

        let color = getLineageColor(variant.variant, subvariant.name);

        featuredHtml += `<div class="subvariant" data-variant="${ variant.variant }" data-lineage="${ subvariant.name }">
              <div class="chart stack-element">
                <div class="stacks">
                  <div class="stack"><div class="block"></div><div class="hatch"></div></div>
                </div>
              </div>
              <div class="chart curve-element">
                <div class="chart-area"></div>
              </div>
              <div class="legend" style="background-color:${ color }"></div>
              <div class="detail">
                <h4 class="name">${ subvariantDisplay }</h4>
                <h6 class="count">${ subvariantTotal.toLocaleString() }</h6>
              </div>
            </div>`;

        if ((columnize) && (columnizeCounter % rowsPerColumn === rowsPerColumn - 1 || subvariantCounter === level.subvariants.length - 1)) {
          featuredHtml += `</div>`;
        }

        columnizeCounter++;

      });

      featuredHtml += `</div>`; // close the subvariant group
      featuredHtml += `</div>`;

    });

  });

  // console.log(locationData.underThresholdLineages);
  let underSvs = locationData.underThresholdLineages.map(sv => {
    // return (lineageToVariantLookup[sv]);
    return {name:sv, ...locationData.subvariantTotals[sv]};
  });

  underSvs.sort((a, b) => b.pct - a.pct);

  // console.log('Under threshold lineages', underSvs.map(sv => `${ sv.name }: ${ pct(sv.pct, 2, true) }`));


  // Handle zero cases

  // NOTE 9.12.22 (paul): turned this off, we will apply the below count to the new element associated with Variant headers in the sections below
  // i.e. ‘Omicron (11 lineages)’
  // ‘Delta (2 lineages)’
  // ‘Other’ —> for Other, put the value in the label next to the small chart: ‘[chart] 240 lineages’
  // featuredSubvariantsCountLabel.textContent = featuredSubvariantCount;
  // if (featuredSubvariantCount === 0) {
  //   featuredSubvariantsCountLabel.textContent = "No";
  // }
  featuredSubvariantContainer.innerHTML = featuredHtml;

  // if (featuredSubvariantCount === 1) {
  //   featuredSubvariantsTab.querySelector(".plural").style.display = "none";
  //   featuredSubvariantsTab.querySelector(".singular").style.display = "block";
  // } else {
  //   featuredSubvariantsTab.querySelector(".plural").style.display = "block";
  //   featuredSubvariantsTab.querySelector(".singular").style.display = "none";
  // }


  if (!gridView) {

    // Activate the charts
    featuredSubvariantContainer.querySelectorAll(".subvariant").forEach(chartContainer => {
      let subvariant = chartContainer.getAttribute("data-lineage");
      let variant = chartContainer.getAttribute("data-variant");

      data.locations.forEach(location => {
        drawSmallChart(location, chartContainer, variant, subvariant);
        drawSmallCurveChart(location, chartContainer, variant, subvariant);
      });
    });

  }

  setLineageButtonListeners();
}


// Subvariant buttons

function setLineageButtonListeners() {

  let container = document.querySelector("#multiples");

  container.querySelectorAll(".subvariant").forEach(div => {

    // Mouseover / touchstart
    div.addEventListener("mouseover", () => subvariantHoverListener(div));
    div.addEventListener("touchstart", async () => subvariantHoverListener(div), {passive:true});

    // Click!
    //div.addEventListener("click", () => subvariantClickListener(div));

    // Mouseout!
    div.addEventListener("mouseout", () => subvariantLeaveListener(div));
  });

  function subvariantHoverListener(div) {

    div.classList.toggle("highlight", true);

    // document.querySelector("#readout-subvariant").classList.toggle("highlight", true);

    // Disable all
    document.querySelectorAll(`.graphic .block`).forEach(block => {
      block.classList.toggle("highlight", false);
    });

    // Highlight
    let lineage = div.getAttribute("data-lineage");
    // let isOtherBucket = lineage.match(/other/gi) ? true : false;

    let variant = div.getAttribute("data-variant");

    // document.querySelectorAll(`#graphic .block[data-lineage="${ lineage }"]`).forEach(block => {
    //   block.classList.toggle("highlight", true);
    // });

    // if (!gridView) {

    if (chartMode === 'pct' || chartMode === 'bar') {
      // setBlockHighlights(document.querySelector(`#graphic .stack-element`), null, variant, lineage);

      document.querySelectorAll(".graphic .stack-element").forEach(stackElement => {
        setBlockHighlights(stackElement, null, variant, lineage);
      });

      document.querySelectorAll(".graphic .stack-element .block").forEach(block => {
        // Make sure it isn't already set as a highlight, eg, opacity:1
        if (block.classList.contains("highlight")) {
          block.style.opacity = 1;
        } else {
          block.style.opacity = 0.2;
        }
      });
    } else if (chartMode === 'curve') {

      document.querySelectorAll(".graphic .curve-element svg").forEach(svg => {
        setCurveChartHighlights(svg, variant, lineage);
      });

      // setCurveChartHighlights(document.querySelector(`#graphic .curve-element svg`), variant, lineage);
    }

    // Readout
    //showSubvariantReadout(subvariantTotals, [lineage], sequencesTotal);


    // Handle button pushbacks


    // Handle categories
    // document.querySelectorAll("#categories .tab").forEach(tab => {
    //   tab.classList.toggle("highlight", false);
    // });
    //
    // if (lineage) {
    //   document.querySelector("#categories-readout").classList.toggle("highlight", true);
    // }

    // // matching variant button activated…
    // let variantButton = document.querySelector(`#variants .variant[data-variant="${ variant }"]`);
    // if (variantButton) {
    //   variantButton.classList.toggle("highlight", true);
    //   variantButton.classList.toggle("pushback", false);
    // } else {
    //   console.log(`could not find a button for ${  variant}`);
    // }

    // }

  }

  function subvariantLeaveListener(div) {

    document.querySelector(".graphic").classList.toggle("highlight", false);
    document.querySelector(".graphic").classList.toggle("highlight-readout", false);

    div.classList.toggle("highlight", false);

    // Disable all
    document.querySelectorAll(`.graphic .block`).forEach(block => {
      block.classList.toggle("highlight", false);
    });

    // blockLeaveListener(document.querySelector("#graphic"));
    blockLeaveListeners();

    if (!gridView) {
      if (document.querySelector(`.graphic .curve-element svg`)) {
        curveLeaveListener(document.querySelector(`.graphic .curve-element svg`));
      }
    }

  }

  // function subvariantClickListener(div) {
  //   let shouldHighlight = !div.classList.contains("focus");
  //
  //   let allButtons = container.querySelectorAll(".subvariant");
  //   let selectedButtons = 0;
  //   allButtons.forEach(button => {
  //     if (button.classList.contains("focus")) {
  //       selectedButtons++;
  //     }
  //   });
  //   let shouldPushback = shouldHighlight || selectedButtons > 1;
  //
  //   // If we're trying to turn a button on, but we've hit the limit, abandon
  //   if (shouldHighlight && selectedButtons >= MAX_SUBVARIANTS_SELECTED) {
  //     return;
  //   }
  //
  //   div.classList.toggle("focus", shouldHighlight);
  //   div.classList.toggle("pushback", false);
  //
  //   // Pushback any non-focused buttons
  //   document.querySelectorAll(`#multiples .subvariant`).forEach(button => {
  //     if (!button.classList.contains("focus")) {
  //       button.classList.toggle("pushback", shouldPushback);
  //     }
  //   });
  //
  //
  //   //document.querySelector("#readout-subvariant").classList.toggle("highlight", shouldHighlight);
  //
  //
  //
  //   // Focus
  //   let lineage = div.getAttribute("data-lineage");
  //   document.querySelectorAll(`#graphic .block[data-lineage="${ lineage }"]`).forEach(block => {
  //     block.classList.toggle("focus", shouldHighlight);
  //     block.classList.toggle("pushback", false);
  //
  //     // let stack = block.parentNode;
  //     // let date = DateTime.fromISO(stack.getAttribute("data-date"));
  //
  //   });
  //
  //   // Disable all highlights
  //   document.querySelectorAll(`#graphic .block`).forEach(block => {
  //     block.classList.toggle("highlight", false);
  //     if (!block.classList.contains("focus")) {
  //       block.classList.toggle("pushback", shouldPushback);
  //     }
  //   });
  //
  //   // Readout
  //   // let subvariantsFocused = [];
  //   // document.querySelectorAll(`#graphic .subvariant.focus`).forEach(sv => subvariantsFocused.push(sv.getAttribute("data-lineage")));
  //   //
  //   // if (subvariantsFocused.length > 0) {
  //   //   showSubvariantReadout(subvariantTotals, subvariantsFocused, sequencesTotal);
  //   // } else {
  //   //   document.querySelector("#readout-subvariant").classList.toggle("highlight", false);
  //   // }
  // }
}

function setChartListeners(container, data, xAxis) {

  //console.log(getEventListeners(container));
  container.addEventListener("mousemove", e => {
    chartMoveListeners(e, container, data, xAxis);
  });

  // container.addEventListener("mouseover", e => {
  //   container.classList.toggle("highlight", true);
  // });

  container.querySelectorAll(".stack-element .error-predictions-sequences").forEach(s => {
    s.addEventListener("click", () => {
      changeChartMode("bar");
      initializeReadout(getDisplayName, getLineageColor, predictionMode && chartMode !== 'bar');
      redraw(getDataCopy());
    });
  });

  container.addEventListener("mouseleave", () => {

    blockLeaveListeners();

    if (gridView) {
      document.querySelectorAll(".graphic:not(#graphic)").forEach((c, i) => {
        c.classList.toggle("highlight", false);
        c.classList.toggle("highlight-readout", false);
      });
    } else {
      document.querySelectorAll("#graphic").forEach((c, i) => {
        c.classList.toggle("highlight", false);
        c.classList.toggle("highlight-readout", false);
      });
    }

    let allDateLabels = document.querySelectorAll(".time .date");
    allDateLabels.forEach(label => label.classList.toggle("hide", false));

    let allDateLines = document.querySelectorAll(".date-lines .date-line");
    allDateLines.forEach(line => line.classList.toggle("hide", false));


    // container.querySelectorAll(".block").forEach(block => {
    //
    //   let color = getLineageColor(block.getAttribute("data-variant"), block.getAttribute("data-lineage"));
    //
    //   block.style.backgroundColor = color;
    //   block.style.opacity = 1;
    // });
  });

  container.addEventListener("touchstart", async e => {
    if (e.touches.length > 0) {
      let touch = e.touches[0];
      chartMoveListeners(touch, container, data, xAxis);

      let touchedElement = document.elementFromPoint(touch.clientX, touch.clientY);
      if (touchedElement.classList.contains("block")) {
        // blockHoverListener(touchedElement, container, data, xAxis);
        blockHoverListeners(touchedElement, data, xAxis);
      } else {
        // blockLeaveListener(container);
        blockLeaveListeners();
      }
    }
  }, {passive:true});
  container.addEventListener("touchmove", async e => {
    if (e.touches.length > 0) {
      let touch = e.touches[0];
      chartMoveListeners(touch, container, data, xAxis);

      let touchedElement = document.elementFromPoint(touch.clientX, touch.clientY);
      if (touchedElement.classList.contains("block")) {
        // blockHoverListener(touchedElement, container, data, xAxis);
        blockHoverListeners(touchedElement, data, xAxis);
      } else {
        // blockLeaveListener(container);
        blockLeaveListeners();
      }
    }
  }, {passive:true});
  container.addEventListener("touchend", async () => {
    let allDateLabels = container.querySelectorAll(".time .date");
    allDateLabels.forEach(label => label.classList.toggle("hide", false));

    let allDateLines = container.querySelectorAll(".date-lines .date-line");
    allDateLines.forEach(line => line.classList.toggle("hide", false));
  }, {passive:true});

  container.addEventListener("mouseout", () => {
    let allDateLabels = container.querySelectorAll(".time .date");
    allDateLabels.forEach(label => label.classList.toggle("hide", false));

    let allDateLines = container.querySelectorAll(".date-lines .date-line");
    allDateLines.forEach(line => line.classList.toggle("hide", false));
  });

}

function chartMoveListeners(e, container, data, xAxis) {

  if (chartMode !== 'bar' && chartMode !== 'pct') {
    return;
  }

  let graphicContainerBoundingBox = JSON.parse(container.getAttribute("data-boundingclientrect"));

  let width = graphicContainerBoundingBox.width;
  let offsetX = graphicContainerBoundingBox.left;

  let xCoord = ((e.clientX - offsetX) / width) * 100;

  let date = xAxis.invert(xCoord);

  // console.log(date);

  if (gridView) {
    document.querySelectorAll(".graphic:not(#graphic)").forEach((c, i) => {
      chartMoveListener(date, c, data.locations[i], xAxis);
    });
  } else {
    document.querySelectorAll("#graphic").forEach((c, i) => {
      chartMoveListener(date, c, data.locations[i], xAxis);
    });
  }

}

function chartMoveListener(date, container, data, xAxis) {

  // console.log(data);


  if (chartMode !== 'bar' && chartMode !== 'pct') {
    return;
  }

  container.classList.toggle("highlight", true);

  // container.classList.toggle("highlight", true);

  // let graphicContainerBoundingBox = JSON.parse(container.getAttribute("data-boundingclientrect"));
  //
  // let width = graphicContainerBoundingBox.width;
  // let offsetX = graphicContainerBoundingBox.left;
  //
  // let xCoord = ((e.clientX - offsetX) / width) * 100;
  //
  // let date = xAxis.invert(xCoord);

  // console.log(date);

  if (date) {

    // This is really, really gross, but fixes a bug where the mouse, being over
    // the space between 2 stacks, would run blockHoverListener, as the hover
    // is triggered from the block, but the incorrect date below is identified,
    // as the date scale is about .1px off from the stacks. Shifting after the
    // '20' hour seems to pave over this issue

    if (date.getHours() >= 20) {
      date.setDate(date.getDate() + 1);
    }

    date = DateTime.fromJSDate(date);
    // console.log('d', date.toISODate());

    let weekStart = d3.timeWeek(date);
    weekStart = DateTime.fromJSDate(new Date(weekStart));
    // console.log(weekStart.toISODate());

    // Highlight the stack for this date, and push back all of the others
    container.querySelectorAll(".stack").forEach(stack => {

      // Highlight for the date
      if (stack.getAttribute("data-date") === weekStart.toISODate()) {
        stack.querySelectorAll(".block").forEach(block => {
          if (block.classList.contains("highlight")) {
            block.style.opacity = 1;
          } else {
            block.style.opacity = 0.6;
          }
        });
      // Pushback for everything else
      } else {
        stack.querySelectorAll(".block").forEach(block => {
          // Make sure it isn't already set as a highlight, eg, opacity:1
          if (block.classList.contains("highlight")) {
            block.style.opacity = 1;
          } else {
            block.style.opacity = 0.2;
          }
        });
      }
    });


    // container.querySelectorAll(".stack").forEach(stack => {
    //   stack.classList.toggle("fadeback", true);
    //   stack.classList.toggle("highlight", false);
    // });
    //
    // container.querySelectorAll(`.stack[data-date="${ weekStart.toISODate() }"]`).forEach(stack => {
    //   //console.log(stack);
    //   if (stack) {
    //     stack.classList.toggle("fadeback", false);
    //     stack.classList.toggle("highlight", true);
    //   }
    // })

    showWeeklyDateReadout(data, xAxis, weekStart, container.querySelector(".stack-element"), container.querySelector(".time"));
  }
}

function setBlockListeners(container, data, xAxis) {

  container.querySelectorAll(".block").forEach(div => {
    // div.addEventListener("mouseover", () => blockHoverListener(div, container, data, xAxis));

    div.addEventListener("mouseover", () => blockHoverListeners(div, data, xAxis));



    // div.addEventListener("touchstart", async e => {
    //   if (e.touches.length > 0) {
    //     console.log('start', div.getAttribute("data-lineage"))
    //     let touch = e.touches[0];
    //     blockHoverListener(div);
    //   }
    // }, {passive:true});
    // div.addEventListener("touchmove", async e => {
    //   if (e.touches.length > 0) {
    //     let touch = e.touches[0];
    //     console.log('touchmove', div.getAttribute("data-lineage"));
    //     blockHoverListener(div);
    //   }
    // }, {passive:true});

    //div.addEventListener("click", () => blockClickListener(div));

    // div.addEventListener("mouseout", () => blockLeaveListener(container));
    div.addEventListener("mouseout", () => blockLeaveListeners());
    //div.addEventListener("touchend", () => blockLeaveListener(div));
  });

}

function blockHoverListeners(highlightedDiv, data, xAxis) {
  if (gridView) {
    document.querySelectorAll(".graphic:not(#graphic) .stacks").forEach((container, i) => {
      blockHoverListener(highlightedDiv, container, data.locations[i], xAxis);
    });
  } else {
    document.querySelectorAll("#graphic .stacks").forEach((container, i) => {
      blockHoverListener(highlightedDiv, container, data.locations[i], xAxis);
    });
  }

  let variant = highlightedDiv.getAttribute("data-variant");
  let subvariant = highlightedDiv.getAttribute("data-lineage");
  highlightSubvariantButtons(variant, subvariant);
}

function blockHoverListener(highlightedDiv, container, data, xAxis) {

  let readoutContainer = container.closest(".graphic").querySelector(".readout");

  let variant = highlightedDiv.getAttribute("data-variant");
  let subvariant = highlightedDiv.getAttribute("data-lineage");

  let isOtherBucket = subvariant.match(/other/gi) ? true : false;

  let stack = highlightedDiv.parentNode;
  let date = DateTime.fromISO(stack.getAttribute("data-date"));

  // console.log(date, stack);

  let readoutData = (predictionMode) ? data.weeklyPredictionData : data.weeklyData;
  let weekData = readoutData.find(d => d.date.toISODate() === date.toISODate());

  if (weekData && weekData[subvariant]) {
    setBlockHighlights(container, highlightedDiv, variant, subvariant);
  } else {
    setBlockHighlights(container, highlightedDiv, '', '');
  }

  container.closest(".graphic").classList.toggle("highlight-readout", true);
  showWeekDataReadout(readoutContainer, data, xAxis, date, variant, subvariant, isOtherBucket);

}

function setBlockHighlights(container, highlightedDiv, variant, subvariant) {

  let highlightedDate;
  if (highlightedDiv) {
    highlightedDate = highlightedDiv.closest('.stack').getAttribute("data-date");
  }

  container.querySelectorAll(`.block`).forEach(div => {

    let color = getLineageColor(div.getAttribute("data-variant"), div.getAttribute("data-lineage"));

    if (color) {
      if (div.getAttribute("data-lineage") === subvariant) {

        if (div.closest(".stack").getAttribute("data-date") === highlightedDate) {
          // Highlight the selected subvariant and stack for this week
          color = color.set('lab.l', '*0.2');
          div.style.backgroundColor = color;
        } else {
          color = color.set('lab.l', '*0.8');
          div.style.backgroundColor = color;
        }

        div.classList.toggle("highlight", true);

      } else {

        div.style.backgroundColor = color;
        div.classList.toggle("highlight", false);
      }
    }

  });


}

function setCurveChartOneTimeListeners() {
  let resizeTimeout;
  window.addEventListener("resize", () => {
    if (chartMode === 'curve') {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(() => {
        redraw(getDataCopy());
      }, 100);
    }
  });
}

function setCurveChartListeners(container, data, xAxis) {

  // let width = container.getBoundingClientRect().width;
  // let left = container.getBoundingClientRect().x;
  // let top = container.getBoundingClientRect().y;

  //console.log(container.getBoundingClientRect());


  // container.addEventListener("mouseover", e=> {
  //   container.classList.toggle("highlight", true);
  // });

  container.addEventListener("mousemove", e => {
    // curveChartMoveListener(e, container, data, xAxis, left, top, width);
    curveChartMoveListener(e, container, data, xAxis);
  });

  container.addEventListener("mouseleave", () => {
    curveLeaveListeners();

    let allDateLabels = document.querySelectorAll(".time .date");
    allDateLabels.forEach(label => label.classList.toggle("hide", false));

    let allDateLines = document.querySelectorAll(".date-lines .date-line");
    allDateLines.forEach(line => line.classList.toggle("hide", false));
  });

  container.querySelectorAll(".curve-element .error-predictions-sequences").forEach(s => {
    s.addEventListener("click", () => {
      changeChartMode("bar");
      initializeReadout(getDisplayName, getLineageColor, predictionMode && chartMode !== 'bar');
      redraw(getDataCopy());
    });
  });

  container.addEventListener("touchstart", async e => {
    if (e.touches.length > 0) {
      let touch = e.touches[0];
      curveChartMoveListener(touch, container, data, xAxis);
    }
  }, {passive:true});
  container.addEventListener("touchmove", async e => {
    if (e.touches.length > 0) {
      let touch = e.touches[0];
      curveChartMoveListener(touch, container, data, xAxis);
    }
  }, {passive:true});
  container.addEventListener("touchend", async () => {
    let allDateLabels = container.closest(".time").querySelectorAll(".date");
    allDateLabels.forEach(label => label.classList.toggle("hide", false));

    let allDateLines = container.closest(".time").querySelectorAll(".date-lines .date-line");
    allDateLines.forEach(line => line.classList.toggle("hide", false));
  }, {passive:true});

}

// function curveChartMoveListeners(e, container, data, xAxis) {
//
//   if (noPredictionData) {
//     return;
//   }
//
//   if (gridView) {
//     document.querySelectorAll(".graphic:not(#graphic)").forEach((c, i) => {
//       curveChartMoveListener(date, c, data.locations[i], xAxis);
//     });
//   } else {
//     document.querySelectorAll("#graphic").forEach((c, i) => {
//       curveChartMoveListener(date, c, data.locations[i], xAxis);
//     });
//   }
//
// }


function curveChartMoveListener(e, container, data, xAxis) {

  if (noPredictionData) {
    return;
  }

  let graphicContainerBoundingBox = JSON.parse(container.getAttribute("data-boundingclientrect"));

  let left = graphicContainerBoundingBox.left;
  let top = graphicContainerBoundingBox.top;
  let width = graphicContainerBoundingBox.width;

  let scrollTop = document.querySelector("main").scrollTop;

  let x = e.clientX - left;
  let y = e.clientY - top + scrollTop;

  // Gets the correct subvariant line
  let {closest} = quadtree.getClosest({x: x, y: y});

  let date = DateTime.fromJSDate(xAxis.invert((x/width)*100));
  date = DateTime.fromISO(date.toISODate());

  if (closest) {

    let subvariant = closest.data.lineage;
    let variant = closest.data.variant;

    if (date && !date.invalid) {

      if (gridView) {
        document.querySelectorAll(".graphic:not(#graphic)").forEach((c, i) => {
          curveHoverListener(date, variant, subvariant, c, data.locations[i], xAxis);
        });
      } else {
        document.querySelectorAll("#graphic").forEach((c, i) => {
          curveHoverListener(date, variant, subvariant, c, data.locations[i], xAxis);
        });
      }

      // curveHoverListener(date, variant, subvariant, container, data, xAxis);

      let isOtherBucket = subvariant.match(/other/gi) ? true : false;
      highlightSubvariantButtons(variant, subvariant, isOtherBucket);
    }

  } else {

    if (date && !date.invalid) {
      // curveHoverListener(date, '', '', container, data, xAxis);

      if (gridView) {
        document.querySelectorAll(".graphic:not(#graphic)").forEach((c, i) => {
          curveHoverListener(date, '', '', c, data.locations[i], xAxis);
        });
      } else {
        document.querySelectorAll("#graphic").forEach((c, i) => {
          curveHoverListener(date, '', '', c, data.locations[i], xAxis);
        });
      }
    }

  }
}

function curveHoverListener(date, variant, subvariant, container, data, xAxis) {

  // console.log(data);

  if (chartMode !== 'curve' || data.noDailyPredictionData) {
    return;
  }

  container.closest(".graphic").classList.toggle("highlight", true);

  let isOtherBucket = subvariant.match(/other/gi) ? true : false;

  setCurveChartHighlights(container, variant, subvariant);

  let closestDot = container.querySelector(`.dotsHighlightLayer .stderrDot[data-date="${ date.toISODate() }"][data-subvariant="${ subvariant }"]`);
  if (closestDot) {
    closestDot.classList.toggle("highlight", true);
    closestDot.style.stroke = closestDot.style.fill;
  }


  let highlightLine = container.querySelector(".curve-element .highlight-line");

  let xCoord = xAxis(date);
  highlightLine.style.left = `${ xCoord.toFixed(2) }%`;

  let dayData = data.dailyPredictionData.find(d => d.date.toISODate() === date.toISODate());

  // if (subvariant && dayData[subvariant]) {
  //   // container.classList.toggle("highlight", true);
  //   container.classList.toggle("highlight-readout", true);
  //   showDayDataReadout(container.querySelector(".readout"), data, xAxis, date, variant, subvariant, isOtherBucket);
  // } else {
  //   container.classList.toggle("highlight-readout", false);
  // }

  showDayDataReadout(container.querySelector(".readout"), data, xAxis, date, variant, subvariant, isOtherBucket);

  if (subvariant && dayData && dayData[subvariant]) {
    container.classList.toggle("highlight-readout", true);
  } else {
    container.classList.toggle("highlight-readout", false);
  }

  showDailyDateReadout(data, xAxis, date, container.querySelector(".curve-element"), container.querySelector(".time"));


}

function setCurveChartHighlights(container, variant, subvariant) {

  // Reset highlight dots
  // let dots = container.querySelectorAll(`.dotsHighlightLayer .stderrDot`);
  // dots.forEach(dot => {
  //   dot.classList.toggle("show", false);
  //   dot.classList.toggle("highlight", false);
  // });
  //
  // // Turn on highlight dots
  // dots = container.querySelectorAll(`.dotsHighlightLayer .stderrDot[data-subvariant="${ subvariant }"]`);
  // dots.forEach(dot => {
  //   dot.classList.toggle("show", true);
  // });

  // Reset highlight dots
  container.querySelectorAll(`.subvariantDotsHighlightLayer`).forEach(layer => {
    layer.classList.toggle("show", false);
  });
  container.querySelectorAll(`.stderrDot.highlight`).forEach(dot => {
    dot.classList.toggle("highlight", false);
  });

  // Turn on highlight dots
  let layer = container.querySelector(`.subvariantDotsHighlightLayer[data-lineage="${ subvariant }"]`);
  if (layer) {
    layer.classList.toggle("show", true);
  }


  // Reset highlight lines
  let lines = container.querySelectorAll(`.linesHighlightLayer .chartLine`);
  lines.forEach(line => {
    line.classList.toggle("show", false);
  });

  // Turn on highlight line
  lines = container.querySelectorAll(`.linesHighlightLayer .chartLine[data-subvariant="${ subvariant }"]`);
  lines.forEach(line => {
    line.classList.toggle("show", true);
  });

  // Reset highlight cones
  let cones = container.querySelectorAll(`.conesHighlightLayer .confidenceArea`);
  cones.forEach(cone => {
    cone.classList.toggle("show", false);
  });

  // Turn on highlight line
  cones = container.querySelectorAll(`.conesHighlightLayer .confidenceArea[data-subvariant="${ subvariant }"]`);
  cones.forEach(cone => {
    cone.classList.toggle("show", true);
  });


  // Push back everything else
  lines = container.querySelector(`.linesLayer`);
  lines.style.opacity = 0.25;

  cones = container.querySelector(`.conesLayer`);
  cones.style.opacity = 0.4;

  let dots = container.querySelector(`.dotsLayer`);
  dots.style.opacity = 0.4;
}

function curveLeaveListeners() {
  if (gridView) {
    document.querySelectorAll(".graphic:not(#graphic)").forEach((c, i) => {
      curveLeaveListener(c);
    });
  } else {
    document.querySelectorAll("#graphic").forEach((c, i) => {
      curveLeaveListener(c);
    });
  }
}

function curveLeaveListener(container) {
  //document.querySelector("#graphic").classList.toggle("highlight", false);
  container.closest(".graphic").classList.toggle("highlight", false);
  container.closest(".graphic").classList.toggle("highlight-readout", false);


  if (!container || chartMode !== 'curve' || noPredictionData) {
    return;
  }

  // Reset highlight dots
  // let dots = container.querySelectorAll(`.dotsHighlightLayer .stderrDot`);
  // dots.forEach(dot => {
  //   dot.classList.toggle("show", false);
  //   dot.classList.toggle("highlight", false);
  // });

  container.querySelectorAll(`.subvariantDotsHighlightLayer`).forEach(layer => {
    layer.classList.toggle("show", false);
  });
  container.querySelectorAll(`.stderrDot.highlight`).forEach(dot => {
    dot.classList.toggle("highlight", false);
  });

  // Reset push backs
  let lines = container.querySelector(`.linesLayer`);
  if (lines) lines.style.opacity = 1;
  let cones = container.querySelector(`.conesLayer`);
  if (cones) cones.style.opacity = 1;
  let dots = container.querySelector(`.dotsLayer`);
  if (dots) dots.style.opacity = 1;

  // Reset highlight lines
  lines = container.querySelectorAll(`.linesHighlightLayer .chartLine`);
  lines.forEach(line => {
    line.classList.toggle("show", false);
  });

  // Reset highlight cones
  cones = container.querySelectorAll(`.conesHighlightLayer .confidenceArea`);
  cones.forEach(cone => {
    cone.classList.toggle("show", false);
  });


  document.querySelectorAll(".tab").forEach(tab => {
    tab.classList.toggle("highlight", false);
  });


  // deactivate all subvariant buttons…
  document.querySelectorAll(`#multiples .subvariant`).forEach(button => {
    button.classList.toggle("highlight", false);
    // if (button.classList.contains("focus")) {
    //   button.classList.toggle("pushback", false);
    // }
  });

  document.querySelectorAll(`#multiples .subvariant`).forEach(button => {
    button.classList.toggle("pushback", false);
  });
}

function highlightSubvariantButtons(variant, subvariant) {

  // Handle Variants
  // Pushback any non-focused buttons
  // document.querySelectorAll(`#variants .variant`).forEach(button => {
  //   if (!button.classList.contains("focus")) {
  //     button.classList.toggle("pushback", true);
  //   }
  // });
  //
  // // matching variant button activated…
  // let variantButton = document.querySelector(`#variants .variant[data-variant="${ variant }"]`);
  // if (variantButton) {
  //   variantButton.classList.toggle("highlight", true);
  //   variantButton.classList.toggle("pushback", false);
  // } else {
  //   //console.log(`could not find a button for ${  variant}`);
  // }



  // Handle categories
  document.querySelectorAll("#categories .tab").forEach(tab => {
    tab.classList.toggle("highlight", false);
  });

  if (subvariant) {
    document.querySelector("#categories-readout").classList.toggle("highlight", true);
  }


  // deactivate all subvariant buttons…
  document.querySelectorAll(`#multiples .subvariant`).forEach(button => {
    button.classList.toggle("highlight", false);
  });

  // Pushback any non-focused buttons
  document.querySelectorAll(`#multiples .subvariant`).forEach(button => {
    if (!button.classList.contains("focus")) {
      button.classList.toggle("pushback", true);
    }
  });

  // matching subvariant button activated…
  let subvariantButton = document.querySelector(`#multiples .subvariant[data-lineage="${ subvariant }"]`);
  if (subvariantButton) {
    subvariantButton.classList.toggle("highlight", true);
    subvariantButton.classList.toggle("pushback", false);
  } else {
    //console.log(`could not find a button for ${  subvariant}`);
  }
}

// function blockClickListener(div, container, data, xAxis) {
//
//   let shouldHighlight = !div.classList.contains("focus");
//
//   let allButtons = document.querySelectorAll("#multiples .subvariant");
//   let selectedButtons = 0;
//   allButtons.forEach(button => {
//     if (button.classList.contains("focus")) {
//       selectedButtons++;
//     }
//   });
//   let shouldPushback = shouldHighlight || selectedButtons > 1;
//
//   // If we're trying to turn a button on, but we've hit the limit, abandon
//   if (shouldHighlight && selectedButtons >= MAX_SUBVARIANTS_SELECTED) {
//     return;
//   }
//
//   document.querySelector("#graphic").classList.toggle("highlight", shouldHighlight);
//
//   let subvariant = div.getAttribute("data-lineage");
//   container.querySelectorAll(`[data-lineage="${ subvariant }"]`).forEach(div => {
//     div.classList.toggle("focus", shouldHighlight);
//     div.classList.toggle("pushback", false);
//   });
//
//   document.querySelectorAll(`#graphic .block`).forEach(block => {
//     if (!block.classList.contains("focus")) {
//       block.classList.toggle("pushback", shouldPushback);
//     }
//   });
//
//   let variant = div.getAttribute("data-variant");
//   let isOtherBucket = subvariant.match(/other/gi) ? true : false;
//
//   let stack = div.parentNode;
//   let date = DateTime.fromISO(stack.getAttribute("data-date"));
//   showWeekDataReadout(data, xAxis, date, variant, subvariant, isOtherBucket);
//   showWeeklyDateReadout(data, xAxis, date);
//
//
//   //
//   // // deactivate all subvariant buttons…
//   // document.querySelectorAll(`#multiples .subvariant`).forEach(button => {
//   //   button.classList.toggle("focus", false);
//   // });
//
//   // matching subvariant button activated…
//   let subvariantButton = document.querySelector(`#multiples .subvariant[data-lineage="${ subvariant }"]`);
//   if (subvariantButton) {
//     subvariantButton.classList.toggle("focus", shouldHighlight);
//     subvariantButton.classList.toggle("pushback", false);
//   } else {
//     //console.log(`could not find a button for ${  subvariant}`);
//   }
//
//   // Pushback any non-focused buttons
//   document.querySelectorAll(`#multiples .subvariant`).forEach(button => {
//     if (!button.classList.contains("focus")) {
//       button.classList.toggle("pushback", shouldPushback);
//     }
//   });
//
// }


function blockLeaveListeners() {
  document.querySelectorAll(".graphic .stacks").forEach(container => {
    blockLeaveListener(container);
  });
}

function blockLeaveListener(container) {
  container.closest(".graphic").classList.toggle("highlight-readout", false);
  //document.querySelector("#graphic").classList.toggle("highlight-date", false);

  container.querySelectorAll(`.block`).forEach(div => {
    let color = getLineageColor(div.getAttribute("data-variant"), div.getAttribute("data-lineage"));
    div.style.backgroundColor = color;
    div.classList.toggle("highlight", false);
    div.style.opacity = 1;
  });


  // document.querySelectorAll(`#variants .variant`).forEach(button => {
  //   button.classList.toggle("pushback", false);
  // });


  document.querySelectorAll(".tab").forEach(tab => {
    tab.classList.toggle("highlight", false);
  });


  // deactivate all subvariant buttons…
  document.querySelectorAll(`#multiples .subvariant`).forEach(button => {
    button.classList.toggle("highlight", false);
    // if (button.classList.contains("focus")) {
    //   button.classList.toggle("pushback", false);
    // }
  });

  document.querySelectorAll(`#multiples .subvariant`).forEach(button => {
    button.classList.toggle("pushback", false);
  });
}









// ----------------------
// Sets

function setTimeframeDates(data) {
  let startRange = document.querySelector("#feature-range--start");
  let connector = document.querySelector("#feature-range--connector");
  let endRange   = document.querySelector("#feature-range--end");

  let startMonthLabel = startDate.toFormat(CHART_DATE_FORMAT_MONTH);
  if (startMonthLabel.length > 4) {
    startMonthLabel = `${startDate.toFormat(CHART_DATE_FORMAT_MONTH_SHORT)  }`;
  }
  startRange.querySelector(".month").innerHTML = startMonthLabel;
  startRange.querySelector(".day").innerHTML = startDate.toFormat(CHART_DATE_FORMAT_DATE_SHORT);
  startRange.querySelector(".year").innerHTML = startDate.toFormat(CHART_DATE_FORMAT_YEAR_SHORT);

  let endMonthLabel = endDate.toFormat(CHART_DATE_FORMAT_MONTH);
  if (endMonthLabel.length > 4) {
    endMonthLabel = `${endDate.toFormat(CHART_DATE_FORMAT_MONTH_SHORT)  }`;
  }
  endRange.querySelector(".month").innerHTML = endMonthLabel;
  endRange.querySelector(".day").innerHTML = endDate.toFormat(CHART_DATE_FORMAT_DATE_SHORT);
  endRange.querySelector(".year").innerHTML = endDate.toFormat(CHART_DATE_FORMAT_YEAR_SHORT);

  let yearsMatch = (startDate.get('year') === endDate.get('year'));
  startRange.querySelector(".year-elements").classList.toggle("hidden", yearsMatch);


  let predictionWrapper = document.querySelector("#feature-prediction-detail");
  let predictionDate = document.querySelector("#feature-prediction-date");

  if (predictionMode && chartMode !== 'bar' && data.locations[0].dailyPredictionData.length > 0) {

    let date = getLastItem(data.locations[0].dailyPredictionData).date;

    let predictionMonthLabel = date.toFormat(CHART_DATE_FORMAT_MONTH);
    if (predictionMonthLabel.length > 4) {
      predictionMonthLabel = `${date.toFormat(CHART_DATE_FORMAT_MONTH_SHORT)  }`;
    }
    predictionDate.querySelector(".month").innerHTML = predictionMonthLabel;
    predictionDate.querySelector(".day").innerHTML = date.toFormat(CHART_DATE_FORMAT_DATE_SHORT);
    predictionDate.querySelector(".year").innerHTML = date.toFormat(CHART_DATE_FORMAT_YEAR_SHORT);

    predictionWrapper.classList.toggle("hidden", false);

  } else {
    predictionWrapper.classList.toggle("hidden", true);
  }

  connector.innerHTML = "&nbsp;-&nbsp;";
  if (startDate.toFormat(CHART_DATE_FORMAT_MONTH) === endDate.toFormat(CHART_DATE_FORMAT_MONTH)) {
    connector.innerHTML = "-";
  }

}

function setUpdateDate() {

  let date = getUpdateDate();

  let updateEls = document.querySelectorAll("#source-data-last-updated, #mobile-source-data-last-updated");
  updateEls.forEach(updateEl => {
    let updateMonthAbbrev = date.toFormat(CHART_DATE_FORMAT_MONTH_SHORT);
    // if (!(updateMonthAbbrev === 'May')) {
    //   updateMonthAbbrev += ".";
    // }
    updateEl.querySelector(".month-abbreviated").innerHTML = updateMonthAbbrev;
    updateEl.querySelector(".day").innerHTML = date.toFormat(CHART_DATE_FORMAT_DATE_SHORT);
    updateEl.querySelector(".year").innerHTML = date.toFormat(CHART_DATE_FORMAT_YEAR_SHORT);
  });
}

export function setCountry(countryName) {
  countries = [countryName];
  clearError();
  setLocalStorage();
  resetLineageIndex(); // Resets the entire lineage index counter
  loadCharts();
}

export function setCountries(_countries, reload) {
  countries = _countries;
  if (reload) {
    clearError();
    setLocalStorage();
    resetLineageIndex(); // Resets the entire lineage index counter
    loadCharts();
  }
}

export function getCountries() {
  return countries;
}

export function setChartMode(mode) {
  chartMode = mode;
}
export function getChartMode() {
  return chartMode;
}



export function setViewTotal(countryTotal) {

  let totalEl = document.querySelector("#feature-total-sequences");
  totalEl.innerHTML = `<span class="value">${ countryTotal.toLocaleString() }</span>&nbsp;${ pluralize('genome', countryTotal) }`;

}



// Simple updates

function updateDataFlags(data) {

  let reportingGapEl = document.querySelector("#reporting-gap");
  let chartMessageEl = document.querySelector("#chart-message");
  let lowCountsEl = document.querySelector("#low-count");

  let m_reportingGapEl = document.querySelector("#mobile-reporting-gap");
  let m_chartMessageEl = document.querySelector("#mobile-chart-message");
  let m_lowCountsEl = document.querySelector("#mobile-low-count");

  reportingGapEl.querySelector("h6").innerHTML = "";
  chartMessageEl.querySelector("h6").innerHTML = "";
  lowCountsEl.querySelector("h6").innerHTML = "";
  m_reportingGapEl.querySelector("h6").innerHTML = "";
  m_chartMessageEl.querySelector("h6").innerHTML = "";
  m_lowCountsEl.querySelector("h6").innerHTML = "";

  reportingGapEl.classList.toggle("show", false);
  chartMessageEl.classList.toggle("show", false);
  lowCountsEl.classList.toggle("show", false);
  m_reportingGapEl.classList.toggle("show", false);
  m_chartMessageEl.classList.toggle("show", false);
  m_lowCountsEl.classList.toggle("show", false);



  // Low data overall

  let lowDataLocations = data.locations.filter(l => l.sequencesFiltered < UNCERTAIN_THRESHOLD);

  if (lowDataLocations.length > 0) {
    let locs = lowDataLocations.map(l => l.location);

    lowCountsEl.querySelector("h6").innerHTML = `Lineage forecasts are unstable for locations with fewer than ${ UNCERTAIN_THRESHOLD.toLocaleString() } genomes generated in the previous 4 months.`;
    lowCountsEl.classList.toggle("show", true);

    m_lowCountsEl.querySelector("h6").innerHTML = `Lineage forecasts are unstable for locations with fewer than ${ UNCERTAIN_THRESHOLD.toLocaleString() } genomes generated in the previous 4 months.`;
    m_lowCountsEl.classList.toggle("show", true);
  } else {

    // Low weekly counts with otherwise good data

    let weeklyData = data.locations[0].weeklyData;
    let uncertainWeeks = weeklyData.filter(d => d.uncertain);

    if ((chartMode === 'bar' || (chartMode === 'pct' && !predictionMode)) && uncertainWeeks.length > 0) {
      if (uncertainWeeks.length === 1) {
        lowCountsEl.querySelector("h6").innerHTML = `The final week has uncertain data`;
        m_lowCountsEl.querySelector("h6").innerHTML = `The final week has uncertain data`;
      } else {
        lowCountsEl.querySelector("h6").innerHTML = `The final ${ uncertainWeeks.length } weeks have uncertain data.`;
        m_lowCountsEl.querySelector("h6").innerHTML = `The final ${ uncertainWeeks.length } weeks have uncertain data.`;
      }
      lowCountsEl.classList.toggle("show", true);
      m_lowCountsEl.classList.toggle("show", true);
    }

  }


  // Predictions

  if (chartMode !== 'bar') {
    chartMessageEl.querySelector("h6").innerHTML = "A multinomial logistic regression model is fitted to GISAID data to estimate the projected growth rates.";
    chartMessageEl.classList.toggle("show", true);

    m_chartMessageEl.querySelector("h6").innerHTML = "The chart above uses a multinomial logistic regression model fitted to GISAID data to estimate the projected growth rates.";
    m_chartMessageEl.classList.toggle("show", true);

    if (predictionMode) {
      chartMessageEl.querySelector("h6").classList.toggle("hidden", false);
      m_chartMessageEl.querySelector("h6").classList.toggle("hidden", false);
    } else {
      chartMessageEl.querySelector("h6").classList.toggle("hidden", true);
      m_chartMessageEl.querySelector("h6").classList.toggle("hidden", true);
    }
  }


  // Old data

  let date = getUpdateDate();

  let dataDiff = date.diff(mostRecentDataDate, ['months']);

  if (dataDiff.values.months >= 1) {
    reportingGapEl.querySelector("h6").innerHTML = `${ countries.join(', ') } has data that is more than one month old.`;
    reportingGapEl.classList.toggle("show", true);

    m_reportingGapEl.querySelector("h6").innerHTML = `${ countries.join(', ') } has data that is more than one month old.`;
    m_reportingGapEl.classList.toggle("show", true);
  }



}





function loadCharts() {
  showLoader();
  document.querySelector("#feature-details").classList.toggle("hide", true);
  document.querySelectorAll(".chart-error-message").forEach(c => c.classList.toggle("show", false));
  fetchData(countries, dataInitialize, handleError, setUpdateDate);
}



export function getStartDate() {
  return startDate;
}
export function getEndDate() {
  return endDate;
}

/* eslint-disable no-unused-vars */
function getTimeframe() {
  return timeframe;
}
function setTimeframe(tf) {
  timeframe = tf;
}
function getFrequency() {
  return frequency;
}
function setFrequency(freq) {
  frequency = freq;
}
/* eslint-enable no-unused-vars */

function filterData(data) {

  endDate = mostRecentDataDate;
  startDate = mostRecentDataDate;

  let tf = getTimeframe();
  let freq = "daily";

  if (tf === "2weeks") {
    startDate = startDate.minus({'weeks':2});
  } else if (tf === "4weeks") {
    startDate = startDate.minus({'weeks':4});
  } else if (tf === "4months") {
    startDate = startDate.minus({'months':4});
    freq = "weekly";
  } else if (tf === "1year") {
    startDate = startDate.minus({'year':1});
    freq = "weekly";
  } else if (tf === "all") {
    freq = "weekly";
  } else { // custom date
    startDate = DateTime.fromISO(tf.start);
    endDate = DateTime.fromISO(tf.end);
    let diff = endDate.diff(startDate, 'days');
    let days = diff.days;
    if (days > 365) {
      freq = "weekly";
    }
  }

  setFrequency(freq);

  let filteredData = {locations:[], variantSummary:[]};

  filteredData.locations = data.locations.map(loc => {

    loc.weeklyData = loc.weeklyData.filter(d => d.date >= startDate && d.date <= endDate);
    loc.dailyData = loc.dailyData.filter(d => d.date >= startDate && d.date <= endDate);
    loc.weeklyPredictionData = loc.weeklyPredictionData.filter(d => d.date >= startDate && d.date <= endDate); // The stack chart shouldn't have future dates
    loc.dailyPredictionData = loc.dailyPredictionData.filter(d => d.date >= startDate);


    //loc.weeklyVariants = loc.weeklyVariants.filter(d => d.date >= startDate && d.date <= endDate);


    let sequencesFiltered = 0;
    loc.weeklyData.forEach(d => {
      sequencesFiltered += d.sequencesCount;
    });
    loc.sequencesFiltered = sequencesFiltered;

    if (sequencesFiltered === 0) {
      loc.noWeeklyData = true;
    }
    //loc.maxPct = d3.max([...loc.data, ...loc.weeklyData], d => d.pct);



    return loc;

  });

  filteredData = gatherOtherBucket(filteredData, LINEAGE_THRESHOLD);

  // Do this after the 'other' buckets are setup
  filteredData.locations.forEach(loc => {

    //loc.variantTotals = getVariantTotals(loc.weeklyVariants, loc.sequencesFiltered);
    loc.subvariantTotals = getSubvariantTotals(loc.weeklyData, loc.sequencesFiltered);

  });

  // if (tf !== "all") {
  //   filteredData.variantSummary = data.variantSummary;
  // }

  return filteredData;

}

// Draw routines

function draw(data) {

  hideLoader();

  document.querySelector("#feature-details").classList.toggle("hide", false);

  let chartData = (predictionMode && chartMode === 'pct' && data.locations[0].weeklyPredictionData.length > 0) ? data.locations[0].weeklyPredictionData : data.locations[0].weeklyData;

  // Initialize the shared xAxis
  let dateExtents = d3.extent(chartData, d => d.date);

  // Include the end date of the last bar w/in the bounds of 0 - 100%,
  // so that everything lines up with the flex-based chart layout
  dateExtents[1] = dateExtents[1].plus({'weeks':1});

  xAxis = d3.scaleTime();
  xAxis.domain(dateExtents)
    .range([0, 100]);



  setGridView();


  // Draw!

  // Grid view -- draw lots of charts
  if (gridView) {

    let gridContainer = document.querySelector("#grid");
    gridContainer.innerHTML = "";

    data.locations.forEach(location => {

      let template = graphicTemplate.cloneNode(true);
      template.setAttribute("id", "");
      let axisTemp = axisTemplate.cloneNode(true);
      let featureTemp = featureTemplate.cloneNode(true);
      featureTemp.setAttribute("id", "");
      // let readoutTemp = readoutTemplate.cloneNode(true);
      // readoutTemp.setAttribute("id", "");

      // All of these outerHTMLs help blow away any event listeners
      let div = document.createElement("div");
      div.classList.toggle("grid-element", true);
      div.innerHTML = `${featureTemp.outerHTML
      }<div class="grid-chart">${  axisTemp.outerHTML  }${template.outerHTML  }</div>`;

      gridContainer.appendChild(div);

      let container = div.querySelector(".graphic");
      let axisContainer = div.querySelector(".axis");
      let featureCountry = div.querySelector(".feature-country");
      featureCountry.setAttribute("id", "");

      let readout = div.querySelector(".readout");
      readout.setAttribute("id", "");

      updateSelectedCountry(location, featureCountry);

      if (chartMode === 'bar' || chartMode === 'pct') {
        drawChart(data, location, container, axisContainer);
      } else if (chartMode === 'curve') {
        drawCurveChart(data, location, container, axisContainer);
      }

    });

    drawAddLocation();

    data.locations.forEach((location, i) => {
      let container = gridContainer.childNodes[i].querySelector(".graphic");
      updateGraphicContainerBoundingBox(container);
    });

    // Add grid related events for adding and removing locations

    document.querySelector("#add-country").addEventListener("click", () => {
      toggleSearch(true);
    });

    document.querySelectorAll(".dismiss-country").forEach(button => {
      button.addEventListener("click", () => {
        let featureEl = button.closest(".feature-country");
        let country = featureEl.getAttribute("data-country");
        let province = featureEl.getAttribute("data-province");

        dismissLocation(data, country, province);
      });
    });

    drawButtons(data);

  // Normal view
  } else {

    data.locations.forEach(location => {

      setViewTotal(location.sequencesFiltered);

      //console.log(location);
      updateSelectedCountry(location, document.querySelector("#feature-country"));

      // We have to rebuild #graphic at this point, to destroy all of its
      // event listeners, and rebuild them again. Otherwise, we get caught in
      // a special circle of hell trying to avoid adding duplicate event listeners
      // every time you change countries, which also ruins performance.
      let template = graphicTemplate.cloneNode(true);
      let container = document.querySelector("#graphic");
      container.outerHTML = template.outerHTML;
      // Have to requery the element from the DOM
      container = document.querySelector("#graphic");

      let axisContainer = document.querySelector("#axis");

      if (chartMode === 'bar' || chartMode === 'pct') {
        drawChart(data, location, container, axisContainer);
      } else if (chartMode === 'curve') {
        drawCurveChart(data, location, container, axisContainer);
      }

      updateGraphicContainerBoundingBox(container);
    });

    drawButtons(data);

  }

}

function setGridView() {
  // Should we be in grid view?
  if (countries.length > 1) {
    gridView = true;
  } else {
    gridView = false;
  }
  document.body.classList.toggle("gridView", gridView);
}

function drawAddLocation() {

  // Add from the template, and outerHTML to blow away any event listeners

  let div = document.createElement("div");
  document.querySelector("#grid").appendChild(div);

  div.outerHTML = countrySearchTemplate.outerHTML;

  if (countries.length >= MAX_LOCATIONS_GRID_MODE) {
    document.body.classList.toggle("max-locations", true);
  } else {
    document.body.classList.toggle("max-locations", false);
  }

}

function dismissLocation(data, country, province) {

  let location = country;
  if (province) {
    location += PROVINCE_SEPARATOR + province;
  }


  // Remove the location from the countries list

  let parsedCountries = parseProvincesAndCountries(countries);

  let locationIndex = parsedCountries.findIndex(c => c.fullId === location);
  if (locationIndex > -1) {
    countries.splice(locationIndex, 1);
  }

  if (countries.length >= MAX_LOCATIONS_GRID_MODE) {
    document.body.classList.toggle("max-locations", true);
  } else {
    document.body.classList.toggle("max-locations", false);
  }

  setCountries(countries, true);

  // // Remove the location from the data list, and set a fresh copy of the data
  // let dataLocationIndex = data.locations.findIndex(l => l.fullId === location);
  // if (dataLocationIndex > -1) {
  //   data.locations.splice(dataLocationIndex, 1);
  // }
  // setDataCopy(data);
  // setLocalStorage();
  //
  // // Remove the chart element from the grid
  // let featureEl = document.querySelector(`.feature-country.${ location }`);
  // if (featureEl) {
  //
  //   let gridEl = featureEl.closest(".grid-element");
  //   gridEl.parentNode.removeChild(gridEl);
  //
  // }
  //
  // setGridView();


}

function updateGraphicContainerBoundingBox(container) {
  //graphicContainerBoundingBox = container.getBoundingClientRect(); // Used for calculations

  container.setAttribute("data-boundingclientrect", JSON.stringify(container.getBoundingClientRect())); // Cache this to speed up hover states later
}

function drawChart(allData, data, container, axisContainer) {

  //let container = document.querySelector("#graphic");

  // Draw the actual chart

  let chartWrapper = container.querySelector(".stack-element");
  let chart = chartWrapper.querySelector(".stacks");
  chart.innerHTML = "";

  // console.log(data);
  if (data.noWeeklyPredictionData && chartMode === 'pct') {
    container.parentNode.classList.toggle("error-prediction", true);
    return;
  }
  if (data.noWeeklyData && chartMode === 'bar') {
    container.parentNode.classList.toggle("error", true);
    return;
  }



  let stackData = predictionMode && chartMode === 'pct' ? data.weeklyPredictionData : data.weeklyData;

  let keys = setupKeys(stackData);

  // Only show valid lineages
  keys = keys.filter(k => !data.underThresholdLineages.includes(k));
  keys = sortKeys(keys, data.allVariants);

  let maxStackValue = d3.max(stackData, d => d.sequencesCount);

  // Y Axis for numeric version
  let yAxis = d3.scaleLinear();
  yAxis.domain([0, maxStackValue]);
  yAxis.range([0, 100]);
  yAxis.nice();

  let stackHTML = ``;
  stackData.forEach(stack => {

    //console.log(stack.date.toISODate(), stack);

    let stackHeightNum = yAxis(stack.sequencesCount);

    let blockHTML = ``;
    keys.forEach(key => {
      let block = stack[key];
      if (block && block.pct) {
        let pct = block.pct;

        let lineage = key;

        let variant = '';
        if (lineageToVariantLookup[lineage]) {
          variant = lineageToVariantLookup[lineage].variant.name;
        } else if (lineage.match(/other/gi)) {
          variant = lineage.replace(/other-/gi, '');
        }

        let color = getLineageColor(variant, lineage);

        blockHTML += `
          <div
            class="block"
            data-num="${ block.num }"
            data-sequencesCount="${ stack.sequencesCount }"
            data-lineage="${ lineage }"
            data-variant="${ variant }"
            style="height:${ (pct * 100).toFixed(2) }%;background-color:${ color }"
          >`;

        // Error bars
        // if (stack.isPrediction) {
        //
        //   let lowerHeight = ((block.lower / block.pct) * 100);
        //   let lowerPos = ((block.pct - block.lower) / 2 / block.pct) * 100;
        //
        //   let upperHeight = ((block.upper / block.pct) * 100);
        //   let upperPos = ((block.pct - block.upper) / 2 / block.pct) * 100;
        //
        //   blockHTML += `
        //       <div
        //         class="block-min"
        //         data-lineage="${ lineage }"
        //         data-variant="${ variant }"
        //         style="height:${ lowerHeight.toFixed(2) }%;top:${ lowerPos.toFixed(2) }%"
        //       >
        //       </div>
        //       <div
        //         class="block-max"
        //         data-lineage="${ lineage }"
        //         data-variant="${ variant }"
        //         style="height:${ upperHeight.toFixed(2) }%;top:${ upperPos.toFixed(2) }%"
        //       >
        //       </div>
        //     `;
        // }

        blockHTML += `
        </div>
      `;

        //stackHeight += pct;
      }
    });

    let uncertainClass = (stack.uncertain) ? "fuzzy" : "";
    let predictionClass = (stack.isPrediction) ? "prediction" : "";

    //stackHTML += `<div class="stack ${ uncertainClass }" data-date="${ stack.date.toISODate() }" style="height:${ (stackHeight * 100).toFixed(2) }%">`;
    stackHTML += `<div
      class="stack ${ uncertainClass } ${ predictionClass }"
      data-date="${ stack.date.toISODate() }"
      data-stackHeightNum="${ stackHeightNum }"
      style="height:${ (stackHeightNum).toFixed(2) }%"
    >`;
    stackHTML += blockHTML;
    stackHTML += `<div class="hatch"></div>`;
    stackHTML += `</div>`;

  });

  chart.innerHTML = stackHTML;

  setBlockListeners(chart, allData, xAxis);
  setChartListeners(container, allData, xAxis);

  drawTimeAxisLabels(xAxis, container);

  if (chartMode === 'bar') {
    drawValueAxisLabels(yAxis, axisContainer.querySelector(".bar-element"), document.querySelector(".stack-element .axis-lines"));
  } else if (chartMode === 'pct') {
    drawValueAxisLabels(yAxis, null, document.querySelector(".stack-element .axis-lines"), null, 6);
  }

}

function drawSmallChart(data, container, variant, subvariant) {

  // Draw the actual chart

  let chartWrapper = container.querySelector(".stack-element");

  if (!chartWrapper) return; // temp

  let chart = chartWrapper.querySelector(".stacks");
  chart.innerHTML = "";

  let stackData = predictionMode && chartMode === 'pct' ? data.weeklyPredictionData : data.weeklyData;

  let keys = setupKeys(stackData);
  // Only show valid lineages
  keys = keys.filter(k => !data.underThresholdLineages.includes(k));
  keys = sortKeys(keys, data.allVariants);



  let stackHTML = ``;
  stackData.forEach(stack => {

    // Find the max stack value, to get setup for the numeric version of the chart
    let maxStackValue = d3.max(stackData, d => d.sequencesCount)

    // Y Axis for numeric version
    let yAxis = d3.scaleLinear();
    yAxis.domain([0, maxStackValue]);
    yAxis.range([0, 100]);
    yAxis.nice();

    //let stackHeightPct = 1;
    let stackHeightNum = yAxis(stack.sequencesCount);


    //let stackHeight = 0;
    let blockHTML = ``;
    keys.forEach(key => {
      let block = stack[key];
      if (block && block.pct) {
        let pct = block.pct;

        let lineage = key;

        let variant = '';
        if (lineageToVariantLookup[lineage]) {
          variant = lineageToVariantLookup[lineage].variant.name;
        } else if (lineage.match(/other/gi)) {
          variant = lineage.replace(/other-/gi, '');
        }

        let color = getLineageColor(variant, lineage);

        if (color) {
          if (key === subvariant) {
            color = color.set('lab.l', '*0.8');
          } else {
            color = color.set('lab.l', '*0.8');
            color = color.alpha(0.15);
            color = color.desaturate(5);
          }
        }

        blockHTML += `
          <div
            class="block"
            data-num="${ block.num }"
            data-sequencesCount="${ stack.sequencesCount }"
            data-lineage="${ lineage }"
            data-variant="${ variant }"
            style="height:${ (pct * 100).toFixed(2) }%;background-color:${ color }"
          >
          </div>
        `;

        //stackHeight += pct;
      }
    });

    let uncertainClass = (stack.uncertain) ? "fuzzy" : "";
    let predictionClass = (stack.isPrediction) ? "prediction" : "";

    stackHTML += `<div
      class="stack ${ uncertainClass } ${ predictionClass }"
      data-date="${ stack.date.toISODate() }"
      data-stackHeightNum="${ stackHeightNum }"
      style="height:${ (stackHeightNum).toFixed(2) }%"
    >`;


    stackHTML += blockHTML;
    stackHTML += `<div class="hatch"></div>`
    stackHTML += `</div>`;

  });

  chart.innerHTML = stackHTML;

  if (subvariant) {
    chart.querySelectorAll(`.block[data-lineage="${ subvariant }"]`).forEach(div => {
      div.classList.toggle("highlight");
    });
  }

}



function drawCurveChart(allData, data, container, axisContainer) {

  //let container = document.querySelector("#graphic");

  //console.log(data);

  // Draw the actual chart
  let chartWrapper = container.querySelector(".curve-element");
  let chart = chartWrapper.querySelector(".chart-area");
  chart.innerHTML = "";

  if (data.noDailyPredictionData) {
    container.parentNode.classList.toggle("error-prediction", true);
    return;
  }

  let margin = {top:2, right:0, bottom:8, left:0};

  const svg = d3.create("svg")
    .attr("width", "100%")
    .attr("height", "100%");

  chart.appendChild(svg.node());

  // Flash briefly to the screen to get valid measurements, and remember
  // the state to get back to
  let remeasureFlag = false;
  if (document.body.classList.contains("pct")) {
    remeasureFlag = "pct";
    document.body.classList.toggle("pct", false);
    document.body.classList.toggle("curve", true);
  } else if (document.body.classList.contains("bar")) {
    remeasureFlag = "bar";
    document.body.classList.toggle("bar", false);
    document.body.classList.toggle("curve", true);
  }

  let bb = svg.node().getBoundingClientRect();
  let width = bb.width;
  let height = bb.height;

  // console.log(width);
  // console.log(chart.getBoundingClientRect());

  // Now, reset everything
  if (remeasureFlag) {
    document.body.classList.toggle(remeasureFlag, true);
    document.body.classList.toggle("curve", false);
  }

  quadtree = new Quadtree(0, 0, width, height, 0, MOUSE_PROXIMITY);

  let svData = data.dailyPredictionData;
  let svProportionsData = data.dailyData;


  // Initialize the shared xAxis
  let dateExtents = d3.extent(svData, d => d.date);

  xAxis = d3.scaleTime();
  xAxis.domain(dateExtents)
    .range([0, 100]);



  let keys = setupKeys(svData);

  keys = sortKeys(keys, data.allVariants); //.reverse();

  //console.log(svData, keys);


  let maxValue = 0;
  svData.forEach(sv => {
    for (const l in sv) {
      if (sv[l].upper) {
        maxValue = Math.max(maxValue, sv[l].upper);
      }
    }
  });
  svProportionsData.forEach(sv => {
    for (const l in sv) {
      if (sv[l].pct) {
        maxValue = Math.max(maxValue, sv[l].pct);
      }
    }
  });

  maxValue = Math.min(1, maxValue);

  //console.log(maxValue);

  let yAxis = d3.scaleLinear();
  yAxis.domain([0, maxValue]);
  yAxis.range([margin.bottom, height - margin.top]);
  yAxis.nice();

  let yDomain = yAxis.domain(); // to get any 'nice'd values

  const probLine = d3.line()
    .x(d => {
      return (xAxis(d.date) * (width / 100)).toFixed(2);
    })
    .y(d => {
      let value = Math.max(0, d.estimate); // set any negative values to 0
      return (yAxis(yDomain[1]) - yAxis(value) + margin.top).toFixed(2);
    });

  const confidenceArea = d3.area()
    .x(d => (xAxis(d.date) * (width / 100)).toFixed(2))
    .y1(d => {
      let value = Math.max(0, d.upper); // set any negative values to 0
      return (yAxis(yDomain[1]) - yAxis(value) + margin.top).toFixed(2);
    })
    .y0(d => {
      let value = Math.max(0, d.lower); // set any negative values to 0
      return (yAxis(yDomain[1]) - yAxis(value) + margin.top).toFixed(2);
    });

  const seDot = d3.symbol()
    .type(d3.symbolCircle)
    .size(8);

  const seDotLg = d3.symbol()
    .type(d3.symbolCircle)
    .size(8);

  let g = svg.append("g")
    .attr("class", "chartLayer");

  let dotsLayer = g.append("g")
    .attr("class", "dotsLayer");

  let conesLayer = g.append("g")
    .attr("class", "conesLayer");

  let linesLayer = g.append("g")
    .attr("class", "linesLayer");

  let dotsHighlightLayer = g.append("g")
    .attr("class", "dotsHighlightLayer");

  let conesHighlightLayer = g.append("g")
    .attr("class", "conesHighlightLayer");

  let linesHighlightLayer = g.append("g")
    .attr("class", "linesHighlightLayer");


  keys.forEach(key => {

    let subvariantData = svData.filter(sv => sv.date >= startDate && sv[key]).map(sv => {
      let variantData = lineageToVariantLookup[key];
      let variant = variantData ? variantData.variant.name : 'other';
      return {
        date : sv.date,
        lineage : key,
        variant : variant,
        ...sv[key]
      }
    });

    let subvariantProportions = svProportionsData.filter(sv => sv.date >= startDate && sv[key]).map(sv => {
      let variantData = lineageToVariantLookup[key];
      let variant = variantData ? variantData.variant.name : 'other';
      return {
        date : sv.date,
        lineage : key,
        variant : variant,
        ...sv[key]
      }
    });


    let color = chroma("#DDDDDD"); // TODO: clean this up
    let variant = '';

    if (lineageToVariantLookup[key]) {
      variant = lineageToVariantLookup[key].variant.name;
      color = getLineageColor(variant, key);
    } else if (key.match(/other/gi)) {
      variant = 'other';
      color = getLineageColor(variant, key);
    }

    let adjustedColor = color;
    let colorLightness = color.get('lab.l');
    if (colorLightness > 87.5) {
      adjustedColor = color.set('lab.l', '*0.9')
    } else if (colorLightness > 70) {
      adjustedColor = color.set('lab.l', '*0.975')
    }

    let adjustedHighlightColor = adjustedColor.set('lab.l', '*0.8');

    let subvariantDotsHighlightLayer = dotsHighlightLayer.append("g")
      .attr("class", "subvariantDotsHighlightLayer")
      .attr("data-lineage", key)
      .attr("data-variant", variant);

    // Draw dots for actual proportions
    subvariantProportions.forEach(d => {
      //console.log(d);


      // Underlying dots
      dotsLayer.append("path")
        .attr("class", `stderrDot`)
        .attr("data-subvariant", key)
        .attr("data-lineage", key)
        .attr("data-variant", variant)
        .attr("data-date", d.date.toISODate())
        .attr("d", seDot())
        .attr("transform", () => {
          return `translate(${ (xAxis(d.date) * (width / 100)).toFixed(2) },${ (yAxis(yDomain[1]) - yAxis(d.pct) + margin.top).toFixed(2) })`;
        })
        .attr("style", `fill:${ adjustedColor }`);

      // Highlight dots
      subvariantDotsHighlightLayer.append("path")
        .attr("class", `stderrDot`)
        .attr("data-subvariant", key)
        .attr("data-lineage", key)
        .attr("data-variant", variant)
        .attr("data-date", d.date.toISODate())
        .attr("d", seDotLg())
        .attr("transform", () => {
          return `translate(${ (xAxis(d.date) * (width / 100)).toFixed(2) },${ (yAxis(yDomain[1]) - yAxis(d.pct) + margin.top).toFixed(2) })`;
        })
        .attr("style", `fill:${ adjustedColor.set('lab.l', '*0.9') }`);
    });

    // Draw confidence curves -- CONES OF UNCERTAINTY
    conesLayer.append("path")
      .attr("class", `confidenceArea`)
      .attr("data-subvariant", key)
      .attr("d", confidenceArea(subvariantData))
      .attr("style", `fill: ${ adjustedColor };`);

    conesHighlightLayer.append("path")
      .attr("class", `confidenceArea`)
      .attr("data-subvariant", key)
      .attr("d", confidenceArea(subvariantData))
      .attr("style", `fill: ${ adjustedColor.set('lab.l', '*0.99') };`);

    // Draw the lines
    linesLayer.append("path")
      .attr("class", `chartLine`)
      .attr("data-subvariant", key)
      .attr("d", probLine(subvariantData))
      .attr("style", `stroke:${ adjustedColor }`);

    // And their highlights
    linesHighlightLayer.append("path")
      .attr("class", `chartLine`)
      .attr("data-subvariant", key)
      .attr("d", probLine(subvariantData))
      // .attr("style", `stroke:${ color.darken(.2) }`);
      .attr("style", `stroke:${ adjustedHighlightColor }`);

    // console.log(subvariantData);

    subvariantData.forEach(d => {
      quadtree.insert({x: xAxis(d.date) * (width / 100), y: yAxis(yDomain[1]) - yAxis(d.pct) + margin.top + 20, w: 3, h: 3, data:d});

      quadtree.insert({x: xAxis(d.date) * (width / 100), y: yAxis(yDomain[1]) - yAxis(d.upper) + margin.top + 20, w: 3, h: 3, data:d});

      quadtree.insert({x: xAxis(d.date) * (width / 100), y: yAxis(yDomain[1]) - yAxis(d.lower) + margin.top + 20, w: 3, h: 3, data:d});
    });
    // subvariantProportions.forEach(d => {
    //   quadtree.insert({x: xAxis(d.date) * (width / 100), y: yAxis(yDomain[1]) - yAxis(d.pct) + margin.top, w: 3, h: 3, data:d});
    // });

  });


  // Testing - to align y axis labels and lines
  // let ticks = yAxis.ticks(YAXIS_TICKS_APPROXIMATE);
  // ticks.forEach(tick => {
  //
  //   let y = (yAxis(yDomain[1]) - yAxis(tick) + margin.top);
  //
  //   g.append("rect")
  //     //.attr("class", "curve-axis-line")
  //     .attr("x", xAxis(xAxis.domain[0]))
  //     .attr("y", y)
  //     .attr("width", 100)
  //     .attr("height", 1)
  //     .attr("stroke", "green");
  //   g.append("text")
  //     .text(tick)
  //     .attr("x", xAxis(xAxis.domain[0]))
  //     .attr("y", y)
  // });



  //setCurveChartListeners(svg.node(), data, xAxis);
  setCurveChartListeners(container, allData, xAxis);

  drawTimeAxisLabels(xAxis, container);
  drawValueAxisLabels(yAxis, axisContainer.querySelector(".curve-element"), document.querySelector(".curve-element .axis-lines"), true);


}

function drawSmallCurveChart(data, container, variant, subvariant) {

  if (noPredictionData) {
    return;
  }

  //let container = document.querySelector("#graphic");

  // Draw the actual chart
  let chartWrapper = container.querySelector(".curve-element");
  let chart = chartWrapper.querySelector(".chart-area");
  chart.innerHTML = "";

  let margin = {top:0, right:0, bottom:2, left:0};

  const svg = d3.create("svg")
    .attr("width", "100%")
    .attr("height", "100%");

  chart.appendChild(svg.node());

  // Flash briefly to the screen to get valid measurements, and remember
  // the state to get back to
  let remeasureFlag = false;
  if (document.body.classList.contains("pct")) {
    remeasureFlag = "pct";
    document.body.classList.toggle("pct", false);
    document.body.classList.toggle("curve", true);
  } else if (document.body.classList.contains("bar")) {
    remeasureFlag = "bar";
    document.body.classList.toggle("bar", false);
    document.body.classList.toggle("curve", true);
  }

  let bb = svg.node().getBoundingClientRect();
  let width = bb.width;
  let height = bb.height;

  // console.log(width);
  // console.log(chart.getBoundingClientRect());

  // Now, reset everything
  if (remeasureFlag) {
    document.body.classList.toggle(remeasureFlag, true);
    document.body.classList.toggle("curve", false);
  }

  //quadtree = new Quadtree(0, 0, width, height, 0, MOUSE_PROXIMITY);

  let svData = data.dailyPredictionData;
  let svProportionsData = data.dailyData;

  let keys = setupKeys(svData);

  keys = sortKeys(keys, data.allVariants); //.reverse();

  //console.log(svData, keys);


  let maxValue = 0;
  svData.forEach(sv => {
    for (const l in sv) {
      if (sv[l].upper) {
        maxValue = Math.max(maxValue, sv[l].upper);
      }
    }
  });
  svProportionsData.forEach(sv => {
    for (const l in sv) {
      if (sv[l].pct) {
        maxValue = Math.max(maxValue, sv[l].pct);
      }
    }
  });

  //console.log(maxValue);

  let yAxis = d3.scaleLinear();
  yAxis.domain([0, maxValue]);
  yAxis.range([margin.bottom, height - margin.top]);
  yAxis.nice();

  let yDomain = yAxis.domain(); // to get any 'nice'd values

  const probLine = d3.line()
    .x(d => {
      return (xAxis(d.date) * (width / 100)).toFixed(2);
    })
    .y(d => {
      return (yAxis(yDomain[1]) - yAxis(d.estimate) + margin.top).toFixed(2);
    });

  let g = svg.append("g")
    .attr("class", "chartLayer");

  let linesLayer = g.append("g")
    .attr("class", "linesLayer");

  // Draw a left axis line
  g.append("rect")
    .attr("class", "curve-axis-line")
    .attr("x", xAxis(xAxis.domain[0]))
    .attr("y", margin.top)
    .attr("width", 1)
    .attr("height", height - margin.top - margin.bottom);

  let linesHighlightLayer = g.append("g")
    .attr("class", "linesHighlightLayer");


  keys.forEach(key => {

    let subvariantData = svData.filter(sv => sv.date >= startDate && sv[key]).map(sv => {
      let variantData = lineageToVariantLookup[key];
      let variant = variantData ? variantData.variant.name : 'other';
      return {
        date : sv.date,
        lineage : key,
        variant : variant,
        ...sv[key]
      }
    });


    let color = chroma("#DDDDDD");
    let variant = '';

    if (key === subvariant && lineageToVariantLookup[key]) {
      variant = lineageToVariantLookup[key].variant.name;
      color = getLineageColor(variant, key);
    } else if (key === subvariant && key.match(/other/gi)) {
      variant = 'other';
      color = getLineageColor(variant, key);
    }

    let adjustedColor = color;
    let colorLightness = color.get('lab.l');
    if (colorLightness > 87.5) {
      adjustedColor = color.set('lab.l', '*0.8')
    } else if (colorLightness > 70) {
      adjustedColor = color.set('lab.l', '*0.95')
    }

    // Draw the lines
    linesLayer.append("path")
      .attr("class", `chartLine`)
      .attr("data-subvariant", key)
      .attr("d", probLine(subvariantData))
      .attr("style", `stroke:${ color }`);

    if (key === subvariant) {
      // And their highlights
      linesHighlightLayer.append("path")
        .attr("class", `chartLine`)
        .attr("data-subvariant", key)
        .attr("d", probLine(subvariantData))
        .attr("style", `stroke:${ adjustedColor }`);
    }


  });


}


function drawTimeAxisLabels(xAxis, outerContainer) {

  let container = outerContainer.querySelector(".time");
  container.innerHTML = "";

  let lineContainer = outerContainer.querySelector(".stack-element .date-lines");
  let lineContainerCurve = outerContainer.querySelector(".curve-element .date-lines");

  let ticks = xAxis.ticks(d3.timeMonth).map(d => {
    return DateTime.fromJSDate(d);
  });

  let tickHTML = '';
  let linesHTML = '';

  ticks.forEach(date => {

    let xPct = xAxis(date);

    tickHTML += `<div class="date" data-date="${ date.toISODate() }" style="left:${ xPct.toFixed(2) }%">`;

    if (gridView) {
      tickHTML += `<p>${ date.toFormat(CHART_DATE_FORMAT_MONTH_SHORT) }</p>`;
    } else {
      tickHTML += `<p>${ date.toFormat(CHART_DATE_FORMAT_MONTH) }</p>`;
    }


    tickHTML += `<p>${ date.toFormat(CHART_DATE_FORMAT_YEAR_SHORT) }</p>
    </div>`;

    linesHTML += `<div class="date-line" data-date="${ date.toISODate() }" style="left:${ xPct.toFixed(2) }%"></div>`;

  });


  //let showToday = (chartMode !== 'bar' && predictionMode);
  let showToday = chartMode === 'curve';

  if (showToday) {
    // Today line and label
    let today = DateTime.now();
    let xCoord = xAxis(today);
    linesHTML += `<div class="date-line today-line" data-date="${ today.toISODate() }" style="left:${ xCoord.toFixed(2) }%"></div>`;
    tickHTML += `<div class="date today-date" data-date="${ today.toISODate() }" style="left:${ xCoord.toFixed(2) }%"">
      <p></p>
      <p>Today</p>
    </div>`;
  }

  // Placeholder for readout
  tickHTML += `<div class="date readout-date"></div>`;

  container.innerHTML = tickHTML;

  lineContainer.innerHTML = linesHTML;
  lineContainerCurve.innerHTML = linesHTML;

  // Now, check today line for overlaps

  if (showToday) {
    let todayLabelRect = container.querySelector(".today-date").getBoundingClientRect();
    let otherLabels = container.querySelectorAll(".date:not(.today-date)");

    otherLabels.forEach(label => {

      let labelRect = label.getBoundingClientRect();

      let overlap = !(todayLabelRect.right < labelRect.left ||
                  todayLabelRect.left > labelRect.right ||
                  todayLabelRect.bottom < labelRect.top ||
                  todayLabelRect.top > labelRect.bottom);

      if (overlap) {
        label.classList.toggle("overlap", true);
      }

    });
  }


}
function drawValueAxisLabels(yAxis, labelContainer, lineContainer, pcts, forceTicks) {

  //let labelContainer = document.querySelector("#axis .bar-element");
  if (labelContainer) {
    labelContainer.innerHTML = "";
  }

  //let lineContainerNum = labelContainer.querySelector(".axis-lines");
  //console.log(labelContainer, lineContainerNum);

  let html = '';
  let linesHTML = '';

  let ticks;
  if (forceTicks) {
    ticks = [...Array(forceTicks).keys()];
  } else {
    ticks = yAxis.ticks(YAXIS_TICKS_APPROXIMATE);
  }

  ticks.forEach((tick, i) => {
    if (pcts) {
      // The curve chart has to have its points mapped by pixels to avoid axis
      // misalignment between svg and html

      let y = (yAxis(yAxis.domain()[1]) - yAxis(tick));

      if (tick !== 0) {
        html += `<li style="top:${ y }px">${ pct(tick, 0, true, true, false)  }</li>`;
      } else {
        html += `<li style="top:${ y }px"></li>`;
      }

      linesHTML += `<div class="axis-line" style="top:${ y }px"></div>`;
      if (i !== 0) {
        //linesHTML += `<div class="axis-line"></div>`;
      }
    } else {

      if (tick !== 0) {
        html += `<li>${ tick.toLocaleString() }</li>`;
      } else {
        html += `<li></li>`;
      }

      linesHTML += `<div class="axis-line"></div>`;
      if (i !== 0) {
        linesHTML += `<div class="axis-line"></div>`;
      }
    }


  });

  if (labelContainer) {
    labelContainer.innerHTML = html;
  }
  lineContainer.innerHTML = linesHTML;


}







// let compareLineageName = (a, b)=>{
//   let aTokens = a.split('.'),
//     bTokens = b.split('.'),
//     aRootLength = a[0].length,
//     bRootLength = b[0].length,
//     // lineage AB comes after Z
//     diff = aRootLength - bRootLength;
//   if (diff === 0) {
//     if (a !== b) {
//       // do the roots differ?
//       diff = a < b ? -1 : 1;
//     } else {
//       // loop through the remaining tokens and compare
//       let limit = Math.min(aTokens.length, bTokens.length);
//       for (let i = 1; i < limit && diff !== 0; i++) {
//         diff = parseInt(aTokens[i]) - parseInt(bTokens[i]);
//       }
//       if (diff === 0) {
//         // case like BA.2 vs BA.2.12.1
//         diff = aTokens.length - bTokens.length;
//       }
//     }
//   }
//   return diff;
// }

let variantIndexCounter = [];
let variantLineageIndexCounter = {};

function initVariantColorKeys(data) {

  let { allVariants, validLineages } = getValidLineages(data, LINEAGE_THRESHOLD);

  // Get rid of unassigned
  let unassignedIndex = allVariants.indexOf("Unassigned");
  if (unassignedIndex > -1) {
    allVariants.splice(unassignedIndex, 1);
  }

  allVariants.reverse(); // 0 starts at the latest variant

  variantIndexCounter = [...allVariants].map(v => v.toLowerCase());
  //variantLineageIndexCounter = {}; // reset!

  initPalette(allVariants, validLineages);

}

function resetLineageIndex() {
  variantLineageIndexCounter = {}; // reset!
}

/* eslint-disable no-unused-vars */
function initPalette(allVariants, validLineages) {

  // console.log(allVariants);
  // console.log(validLineages);


  if (useStaticPalette) {
    // Use a list of lineages to drive the palette, to ensure colors match
    // from chart to chart

    let lineages = lineagesJson.map(l => {
      return lineageToVariantLookup[l];
    });


    // Now, add any lineages we know about to the grouping
    let mappedValidLineages = validLineages.filter(vl => !vl.match(/other/gi)).map(vl => {
      let v = lineageToVariantLookup[vl];
      return v;
    });

    lineages = [...lineages, ...mappedValidLineages];

    let lineageGroups = groupBy(lineages, 'secondLevel');
    let startRowColorTable = 14;

    //console.log(lineageGroups);
    Object.keys(lineageGroups).forEach((g, i) => {
      palette.registerPaletteConfig(g, palette.getColorRange((startRowColorTable+(i*5))%24, 'flat'));
    });


    // Requesting any lineages in lineages.json from the color system, so they're cached
    // This is the most effective way to force the color system
    lineagesJson.forEach(lineage => {

      let lineageData = lineageToVariantLookup[lineage];
      let lineageKey, variant;

      if (lineageData) {

        variant = lineageData.variant.name;

      }

      let c = getLineageColor(variant, lineage);
      //console.log(lineage, c.toString());
    });

    palette.registerPaletteConfig('unassigned', palette.getColorRange(21, 'flat'));

    let otherColor = palette.getColorRange(startRowColorTable, 'flat')[4];
    otherColor = otherColor.desaturate(5);
    palette.registerPaletteConfig('other', [
      otherColor
    ]);


  } else {
    // Use a dynamic palette assignment that's based only on this country or province

    palette.registerPaletteConfig(0, palette.getColorRange(21, 'buhdegah-cat', {columnsToUse: 1, rowsToStep: 6}));
    palette.registerPaletteConfig(1, palette.getColorRange(8, 'buhdegah-cat', {columnsToUse: 1, rowsToStep: 1}));
    palette.registerPaletteConfig(2, palette.getColorRange(10, 'buhdegah-cat', {columnsToUse: 1, rowsToStep: 1}));
    palette.registerPaletteConfig(3, palette.getColorRange(12, 'buhdegah-cat', {columnsToUse: 1, rowsToStep: 1}));

    palette.registerPaletteConfig('unassigned', palette.getColorRange(21, 'flat'));

    let otherColor = palette.getPaletteConfig(0)[4];
    otherColor = otherColor.desaturate(5);
    palette.registerPaletteConfig('other', [
      otherColor
    ]);

  }

}
/* eslint-enable no-unused-vars */

export function getVariantColorKey(variant) {

  //console.log(variantIndexCounter);

  variant = variant || '';

  let variantKey = '';

  if (variant && !(variant === "" || variant.toLowerCase() === "unassigned" || variant.toLowerCase() === "other")) {

    let variantIndex = variantIndexCounter.indexOf(variant.toLowerCase());
    if (variantIndex < 0) {
      //variantIndexCounter.push(variant.toLowerCase());
      variantIndex = variantIndexCounter.indexOf(variant.toLowerCase());
    }

    variantKey = `${ variantIndex }`;

  } else {
    variantKey = variant.toLowerCase();
  }

  return `${ variantKey.toLowerCase() }`;

}
export function getVariantPalette(variant) {
  let colorKey = getVariantColorKey(variant);
  return palette.getPaletteConfig(colorKey);
}
export function getLineageColorKey(lineage) {

  let lineageData = lineageToVariantLookup[lineage];
  let lineageKey, variant;

  if (lineageData) {

    variant = lineageData.variant.name;

    let lineageTokens = lineage.split('.');
    let lineagePart = '';

    if (lineageTokens.length > 1) {
      lineagePart = lineageTokens.slice(1).join('.');
      //console.log(lineagePart, lineage);
    }

    let variantPart = variant; //`${lineageTokens[0]  }`;

    variantLineageIndexCounter[variantPart] = variantLineageIndexCounter[variantPart] || [];


    let lineageIndex = variantLineageIndexCounter[variantPart].indexOf(lineagePart);
    if (lineageIndex < 0) {
      variantLineageIndexCounter[variantPart].push(lineagePart);
      lineageIndex = variantLineageIndexCounter[variantPart].indexOf(lineagePart);
    }

    lineageIndex = lineageIndex % 32;

    //let altKey = walkPalette(variantIndex, lineageIndex);

    lineageKey = `${ lineageIndex }`;

    //lineageKey = altKey;

    //lineageKey = colorByLineage(variant, lineage);

  } else {

    if (!lineage || lineage === "" || lineage.toLowerCase() === "Unassigned") {
      lineageKey = "unassigned";
    } else {
      lineageKey = "other";
    }

  }

  return lineageKey;

}

export function getLineageColor(variant, lineage) {

  if (colorTableJson[lineage]) {
    let labColor = colorTableJson[lineage].lab_color;
    return chroma.lab(labColor[0], labColor[1], labColor[2]);
  } else {
    return chroma("#C2C2C2");
  }

}


export function getLineageColor__old(variant, lineage) {

  let lineageColorKey = getLineageColorKey(lineage);

  //console.log(lineage, lineageColorKey);

  let paletteConfig;

  if (useStaticPalette) {

    //let paletteConfig = getVariantPalette(variant);
    let lineageData = lineageToVariantLookup[lineage];
    if (lineageData) {
      let key = lineageData.secondLevel;
      paletteConfig = palette.getPaletteConfig(key);
    } else {
      if (lineageColorKey === "unassigned") {
        console.log("here");
        paletteConfig = palette.getPaletteConfig("unassigned");
      } else if (lineageColorKey === "other") {
        paletteConfig = palette.getPaletteConfig("other");
      }
    }

  } else {

    paletteConfig = getVariantPalette(variant);
    if (!paletteConfig) {
      if (lineageColorKey === "unassigned") {
        //console.log("here");
        paletteConfig = getVariantPalette("unassigned");
      } else if (lineageColorKey === "other") {
        paletteConfig = getVariantPalette("other");
      }
    }

  }



  //console.log('getLineageColor', variant, paletteConfig, lineage, lineageColorKey);
  // console.log('getVariantColorKey', getVariantColorKey(variant));

  let color;
  if (lineageColorKey.match(/other/gi)) {
    color = getLastItem(paletteConfig);
  } else {
    color = paletteConfig[lineageColorKey];
  }

  return color;
}


export function getDisplayName(variantOrLineage) {

  let display = variantOrLineage || '';
  if (display.match(/unassigned|untagged/gi)) {
    display = "Unassigned";
  } else if (!display.match(/\./gi)) {
    display = display.toLowerCase();
    display = capitalize(display);
  }
  return display;

}

/* eslint-disable no-console */
function handleError(err) {
  console.log(err);
  console.log(err.message);
  document.body.classList.toggle("error", true);
  document.body.classList.toggle("loading", false);

  document.querySelector("#error").classList.toggle("hidden", false);

}
function handleErrorPredictions() {
  document.querySelector("#error").classList.toggle("hidden", true);
  document.body.classList.toggle("error-prediction", true);
}
/* eslint-enable no-console */

/* eslint-disable no-unused-vars */
function clearError() {
  document.body.classList.toggle("error", false);
  document.body.classList.toggle("error-prediction", false);
}
/* eslint-enable no-unused-vars */
